import {
  View,
  Pressable,
  Linking,
  Alert,
  Modal,
  ScrollView,
  ActivityIndicator,
  Platform,
  Dimensions,
  Image,
  InteractionManager,
  AppState,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
  Component,
  ErrorInfo,
  ReactNode,
  type MutableRefObject,
} from 'react';
import { useRouter } from 'expo-router';
import { useTheme } from '@/theme';
import { useUserStore, type UserProfile } from '@/state/userStore';
// TripPlanningActionsBar merged into ItineraryView header
import { ImportSheet } from '@/components/planning/ImportSheet';
import type { LocationCoordinates } from '@/services/maps';
import { ItineraryView } from '@/components/itinerary/ItineraryView';
import { PartnerMarketplace } from '@/components/marketplace/PartnerMarketplace';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { ThemedText, Body, Caption, SubHeading } from '@/components/ui/Text';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { Ionicons } from '@expo/vector-icons';
import { LoadingState } from '@/components/ui/LoadingState';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { TripChecklist } from '@/components/checklist/TripChecklist';
import { updateTrip, type Trip, type CollaboratorPermission, type TripDestination } from '@/services/trips';
import type { Booking } from '@/services/bookings';
import type { ItineraryItem, ItineraryItemCategory } from '@/services/itinerary';
import type { ChatPlannerResult, ChatPlannerSuggestion, LocalPicksRecommendationItem } from '@/services/aiPlanner';
import {
  dedupeLocalPicksByPlaceId,
  mapCityPicksToRecommendations,
  requestCityPicks,
} from '@/services/cityPicks';
import { checkVisaRequirement, getVisaStatusMessage as getVisaStatusMessageLegacy } from '@/utils/visaCheck';
import {
  buildAiCitySections,
  filterDuplicateSuggestions,
  formatCategoryLabel,
  mapCategoryToActivityType,
} from '@/utils/suggestionHelpers';
import { buildPlannerFocusCacheKey, getCachedChatPlannerResult } from '@/utils/aiSuggestionCache';
import { getCachedTripData } from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { logger } from '@/services/logger';
import { normalizeAttachments, prefetchUpcomingAttachments } from '@/services/itineraryAttachments';
import { getContextualRecommendations, type PlaceRecommendation, type RecommendationContext } from '@/services/recommendations';
import { PlaceRatingBadge } from '@/components/places/PlaceRatingBadge';
import { hasCollaborators, type TripAlert } from '@/utils/tripAlerts';
import { getClearedAlerts } from '@/utils/clearedAlerts';
import { getTripAlertReadTimestamps } from '@/utils/tripAlertReadPersistence';
import { useToast } from '@/components/ui/ToastProvider';
import { isInternalDebugBuild, isUiDebugLoggingEnabled } from '@/services/logger';
import type { VisaRequirement } from '@/services/visaChecker';
import { VisaChecklist } from '@/components/planning/VisaChecklist';
import { getTravelRequirements } from '@/services/travelRequirements';
import { hasPremiumAccess } from '@/services/entitlements';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import type { AiPlanIdeaCardsQuotaView } from '@/services/usageCounters';
import { FREE_PLAN_LIMITS, PLAN_AI_IDEA_BATCH } from '@/types/entitlements';
import { COUNTRY_ISO2_OPTIONS, fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';
import { MaybeContainer } from '@/components/planning/MaybeContainer';
import { createMaybeItem, type MaybeItem } from '@/services/maybeItems';
import { useMaybeItems } from '@/hooks/useMaybeItems';
import { SmartRemindersPanel } from '@/components/planning/SmartRemindersPanel';
import { PlaybookSection } from '@/components/planning/PlaybookSection';
import { coerceDate } from '@/utils/dateKeys';
import { deriveTripDateRange, expandDateRangeToDays } from '@/utils/tripDateRange';
import { getTripDestinationLabel } from '@/utils/tripDestination';
import { effectiveTopInset } from '@/utils/effectiveTopInset';
import { useNotificationStore } from '@/notifications/store';
import * as Updates from 'expo-updates';
/** Stable reference for Zustand (avoid new selector fn each render → useSyncExternalStore depth loops). */
const selectHasPremiumAccess = (s: ReturnType<typeof useUserStore.getState>) =>
  hasPremiumAccess({
    isPremium: s.profile?.isPremium,
    planTier: s.profile?.planTier,
    entitlement: s.profile?.entitlement,
  });

type VisaCheckResult = ReturnType<typeof checkVisaRequirement> | null;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const EMPTY_BOOKINGS: Booking[] = [];
type IdeasTab = 'ai' | 'localPicks' | 'for_you' | 'packages';
type LegacyIdeasTab = IdeasTab | 'amadeus';

const normalizeIdeasTab = (tab?: LegacyIdeasTab): IdeasTab => {
  if (!tab || tab === 'amadeus') return 'localPicks';
  return tab;
};

const isStagingDebug = isUiDebugLoggingEnabled();

type PlanningTabProps = {
  trip: Trip;
  itineraryItems: ItineraryItem[];
  itineraryLoading?: boolean;
  bookings: Booking[];
  isArchived: boolean;
  canEdit: boolean;
  userId: string;
  userPermission?: CollaboratorPermission | 'owner' | null;
  isOwner?: boolean;
  onAddItem: (date?: Date) => void;
  onEditItem: (item: ItineraryItem) => void;
  onDeleteItem: (item: ItineraryItem) => void;
  onViewDocument: (url: string, meta?: { offlinePath?: string | null; fileName?: string; contentType?: string | null }) => void;
  // Inbox sync
  onOpenEmailProvider?: () => void;
  inboxSyncing?: boolean;
  inboxConnected?: boolean | null;
  inboxProviderLabel?: string;

  // Copilots
  onLaunchRebooking?: () => void;
  onLaunchDisruption?: () => void;
  onLaunchBudget?: () => void;
  copilotLoading?: boolean;
  onOpenTravelCompanion?: () => void;
  // AI Suggestions
  aiSuggestions?: ChatPlannerResult | null;
  aiSuggestionsLoading?: boolean;
  aiSuggestionsError?: string | null;
  aiSuggestionsErrorMode?: 'itinerary' | 'packages' | null;
  aiSuggestionsLoadingExtended?: boolean;
  aiSuggestionsRequestMode?: 'itinerary' | 'packages' | null;
  onGenerateSuggestions?: (mode?: 'itinerary' | 'packages') => void;
  onCancelGenerateSuggestions?: () => void;
  // Add recommendation to itinerary
  onAddRecommendationToItinerary?: (item: {
    title: string;
    description?: string;
    category: ItineraryItemCategory;
    startDate: Date;
    startTime?: string; // Time string (HH:mm)
    endTime?: string; // Time string (HH:mm)
    address?: string;
    location?: string;
  }) => Promise<void>;
  onApplyAllSuggestions?: () => void;
  applyingSuggestions?: boolean;
  onAddSingleSuggestion?: (
    suggestion: any,
    picked?: { date: Date; startTime?: string; endTime?: string }
  ) => void;
  addingSuggestionIds?: Set<string>;
  EnhancedSuggestionCard?: React.ComponentType<any>;
  mapToEnhancedSuggestion?: (suggestion: any, destination?: string) => any;
  profile?: Pick<
    UserProfile,
    'travelType' | 'citizenship' | 'homeCountry' | 'citizenshipIso2' | 'homeCountryIso2'
  >;
  onSearchFlights?: () => void;
  onSearchHotels?: () => void;

  // Smart upload
  onSmartUpload?: () => void;
  smartUploadLoading?: boolean;
  // Optional: when the status banner suggests offline download, route somewhere useful
  onOpenOfflineDownload?: () => void;
  // Readiness alerts
  readinessAlerts?: TripAlert[];
  onOpenAlerts?: () => void;
  onAlertAction?: (alert: TripAlert) => void;
  readinessCollapsed?: boolean;
  onToggleReadiness?: (collapsed: boolean) => void;
  /** Controls whether the inline "items needing attention" strip is shown above itinerary. */
  showTripOverviewCard?: boolean;

  // Optional: when set (e.g. header comment icon tap), expand the Maybe section (suggest something)
  openCollaboratorSuggestToken?: number;
  // Optional: expand the trip comments section from the parent header action.
  openCommentsToken?: number;

  /**
   * “Maybe → Schedule” handler. Should prompt for date/time and only resolve true when the item
   * has been added to the itinerary successfully.
   */
  onScheduleMaybeItem?: (item: MaybeItem) => Promise<boolean> | boolean;

  /** Notifies parent when Ideas & AI modal opens/closes (e.g. floating “still updating” chip on Plan). */
  onIdeasModalVisibilityChange?: (visible: boolean) => void;
  /** Increment to reopen the Ideas modal from outside PlanningTab (e.g. chip tap). */
  ideasModalReopenToken?: number;
  /** Called after offline trip pack is downloaded or removed (parent can refresh Plan alerts). */
  onTripOfflineCacheChanged?: () => void;
  /** Parent-owned ref: focus line sent as chatPlanner `intentAppendix` (priority chips). */
  aiPlannerIntentAppendixRef?: MutableRefObject<string | null>;
  /**
   * Free-plan Plan AI (chatPlanner) card quota; omit when the user has premium (no meter).
   * `null` before the Firestore snapshot arrives.
   */
  planIdeaCardsQuota?: AiPlanIdeaCardsQuotaView | null;
  /** Pull latest itinerary rows from Firestore (e.g. after template date repair) so UI is not stale until app resume. */
  refreshPlanItineraryFromServer?: () => Promise<void>;
};
type PlanningTabBodyProps = PlanningTabProps & {
  safeTripStartDate: Date;
  safeTripEndDate: Date;
  isStagingDebug: boolean;
  tripWithOwner: Trip;
};


class ComponentErrorBoundary extends Component<
  { children: ReactNode; fallback?: ReactNode; name?: string },
  { hasError: boolean; error: Error | null; retryCount: number }
> {
  constructor(props: { children: ReactNode; fallback?: ReactNode; name?: string }) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error) {
    logger.error('[PlanningTab] ComponentErrorBoundary getDerivedStateFromError', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('[PlanningTab] ComponentErrorBoundary componentDidCatch', error, errorInfo);
  }

  handleRetry = () => {
    this.setState((prev) => ({ hasError: false, error: null, retryCount: prev.retryCount + 1 }));
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <View style={{ padding: 16, gap: 8 }}>
          <ThemedText variant="bodyStrong" color="#ff6b6b">
            Something went wrong loading this section.
          </ThemedText>
          {this.state.error && (
            <Body color="#666">
              {this.state.error.message}
            </Body>
          )}
          {this.state.retryCount < 2 && (
            <Pressable
              onPress={this.handleRetry}
              style={{ marginTop: 4, paddingVertical: 6, paddingHorizontal: 12, alignSelf: 'flex-start', backgroundColor: '#333', borderRadius: 6 }}
              accessibilityLabel="Retry loading this section"
            >
              <ThemedText variant="body" color="#fff">Retry</ThemedText>
            </Pressable>
          )}
        </View>
      );
    }

    return this.props.children;
  }
}

function PlanningTabBody({
  trip,
  itineraryItems,
  itineraryLoading = false,
  bookings,
  isArchived,
  canEdit,
  userId,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  onOpenEmailProvider,
  inboxSyncing,
  inboxConnected,
  inboxProviderLabel,
  onLaunchRebooking,
  onLaunchDisruption,
  onLaunchBudget,
  copilotLoading,
  aiSuggestions,
  aiSuggestionsLoading,
  aiSuggestionsError,
  aiSuggestionsErrorMode,
  aiSuggestionsLoadingExtended,
  aiSuggestionsRequestMode,
  onGenerateSuggestions,
  onCancelGenerateSuggestions,
  onAddRecommendationToItinerary,
  onApplyAllSuggestions,
  applyingSuggestions,
  onAddSingleSuggestion,
  addingSuggestionIds,
  EnhancedSuggestionCard,
  mapToEnhancedSuggestion,
  profile,
  onSearchFlights,
  onSearchHotels,
  onSmartUpload,
  smartUploadLoading,
  onOpenTravelCompanion,
  onOpenOfflineDownload,
  readinessAlerts,
  onOpenAlerts,
  onAlertAction,
  readinessCollapsed,
  onToggleReadiness,
  showTripOverviewCard = true,
  openCollaboratorSuggestToken,
  openCommentsToken,
  onScheduleMaybeItem,
  onIdeasModalVisibilityChange,
  ideasModalReopenToken,
  onTripOfflineCacheChanged,
  aiPlannerIntentAppendixRef,
  planIdeaCardsQuota,
  refreshPlanItineraryFromServer,
  userPermission: _userPermission,
  isOwner: _isOwner,
  safeTripStartDate,
  safeTripEndDate,
  isStagingDebug,
  tripWithOwner,
}: PlanningTabBodyProps) {
  logger.debug('[PlanningTab] Trip validation passed', {
    itineraryItemsCount: itineraryItems?.length,
    bookingsCount: bookings?.length,
  });

  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height: screenHeight } = Dimensions.get('window');
  const bottomSheetMaxHeight = useMemo(
    () => Math.max(Math.min(screenHeight - insets.top - 60, screenHeight * 0.95), screenHeight * 0.6),
    [insets.top, screenHeight],
  );
  const router = useRouter();

  const [localPicksRecommendations, setLocalPicksRecommendations] = useState<LocalPicksRecommendationItem[]>([]);
  const [localPicksLoading, setLocalPicksLoading] = useState(false);
  const [localPicksError, setLocalPicksError] = useState<string | null>(null);
  const [localPicksRefreshToken, setLocalPicksRefreshToken] = useState(0);
  const [maybeRefreshToken, setMaybeRefreshToken] = useState(0);
  const [ideasModalVisible, setIdeasModalVisible] = useState(false);
  const ideasModalReopenHandledRef = useRef(0);
  const collabSuggestHandledRef = useRef(0);
  const openCommentsTokenHandledRef = useRef(0);
  const [ideasTab, setIdeasTab] = useState<IdeasTab>('localPicks');
  const [ideasTargetDay, setIdeasTargetDay] = useState<number | undefined>();
  const [importVisible, setImportVisible] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | undefined>();
  const suggestionIds = addingSuggestionIds ?? new Set<string>();
  const safeBookings = useMemo(() => bookings ?? EMPTY_BOOKINGS, [bookings]);
  const [forYouCount, setForYouCount] = useState(0);
  const { items: maybeItems } = useMaybeItems({
    tripId: trip?.id ?? '',
    userId,
    status: 'pending',
    refreshToken: maybeRefreshToken,
  });
  const maybeIdeasCount = maybeItems.length;
  const [tripToolsExpandedKey, setTripToolsExpandedKey] = useState<
    'book' | 'checklist' | 'maybe' | 'assist' | 'services' | null
  >(null);
  const [commentsExpanded, setCommentsExpanded] = useState(false);

  const handleOpenTravelCompanion = useCallback(() => {
    onOpenTravelCompanion?.();
  }, [onOpenTravelCompanion]);

  const toggleTripTool = useCallback((key: 'book' | 'checklist' | 'maybe' | 'assist' | 'services') => {
    setTripToolsExpandedKey((prev) => (prev === key ? null : key));
  }, []);

  // External shortcut: open Ideas when header sparkles is tapped.
  // Token 0 is not a signal (initial state); only react when user bumps the token (see ideasModalReopenToken).
  useEffect(() => {
    if (typeof openCollaboratorSuggestToken !== 'number') return;
    const t = openCollaboratorSuggestToken;
    if (t <= 0 || t === collabSuggestHandledRef.current) return;
    collabSuggestHandledRef.current = t;
    // Keep collaborative context visible while still honoring the user action
    // to open the Ideas surface from the header icon.
    if (trip?.collaborators && trip.collaborators.length > 0) {
      setTripToolsExpandedKey('maybe');
    }
    setIdeasTab('localPicks');
    setIdeasModalVisible(true);
  }, [openCollaboratorSuggestToken, trip?.id, trip?.collaborators?.length]);

  useEffect(() => {
    if (typeof openCommentsToken !== 'number') return;
    const t = openCommentsToken;
    if (t <= 0 || t === openCommentsTokenHandledRef.current) return;
    openCommentsTokenHandledRef.current = t;
    setCommentsExpanded(true);
  }, [openCommentsToken, trip?.id]);

  useEffect(() => {
    onIdeasModalVisibilityChange?.(ideasModalVisible);
  }, [ideasModalVisible, onIdeasModalVisibilityChange]);

  useEffect(() => {
    return () => {
      onIdeasModalVisibilityChange?.(false);
    };
  }, [onIdeasModalVisibilityChange]);

  useEffect(() => {
    ideasModalReopenHandledRef.current = 0;
    collabSuggestHandledRef.current = 0;
    openCommentsTokenHandledRef.current = 0;
  }, [trip?.id]);

  useEffect(() => {
    const t = ideasModalReopenToken ?? 0;
    if (t <= 0 || t === ideasModalReopenHandledRef.current) return;
    ideasModalReopenHandledRef.current = t;
    setIdeasModalVisible(true);
  }, [ideasModalReopenToken]);

  // Log component lifecycle in development
  useEffect(() => {
    logger.debug('[PlanningTab] Component mounted/updated', { tripId: trip?.id, destination: trip?.destination });
    logger.debug('[PlanningTab] ItineraryView component available', { type: typeof ItineraryView });
  }, [trip?.id]);

  const getDestinationCity = useCallback((dest: any): string | undefined => {
    const city =
      (typeof dest?.city === 'string' && dest.city.trim()) ||
      (typeof dest?.name === 'string' && dest.name.trim()) ||
      (typeof dest?.location === 'string' && dest.location.trim()) ||
      undefined;
    return city || undefined;
  }, []);

const primaryDestination = useMemo(() => {
  const legacy = typeof trip.destination === 'string' ? trip.destination.trim() : '';
  if (legacy) return legacy;
  const first = trip.destinations?.[0];
  return first ? getDestinationCity(first) : undefined;
}, [getDestinationCity, trip.destination, trip.destinations]);

const primaryDestinationCoordinates = useMemo<LocationCoordinates | undefined>(() => {
  const destWithCoords = trip.destinations?.find((dest) => dest.coordinates);
  if (destWithCoords?.coordinates) {
    return destWithCoords.coordinates;
  }
  return undefined;
}, [trip.destinations]);

const localPicksDestinations = useMemo(() => {
  const raw = trip.destinations || [];
  const destinations = raw
    .map((d: any) => {
      const city = getDestinationCity(d);
      if (!city) return null;
      return { ...d, city };
    })
    .filter(Boolean) as any[];
  if (destinations.length) return destinations as any;
  // Fallback: single-city legacy destination
  if (primaryDestination) {
    return [
      {
        city: primaryDestination,
        startDate: safeTripStartDate ?? new Date(),
        endDate: safeTripEndDate ?? safeTripStartDate ?? new Date(),
        coordinates: primaryDestinationCoordinates,
      } as any,
    ];
  }
  return [];
}, [
  trip.destinations,
  safeTripStartDate,
  safeTripEndDate,
  primaryDestination,
  primaryDestinationCoordinates,
  getDestinationCity,
]);

const selectedCityForLocalPicks = useMemo(() => {
  // If a specific day is selected, choose the destination segment covering that day.
  if (selectedDay && localPicksDestinations.length) {
    if (!safeTripStartDate) return null;
    const dayDate = new Date(safeTripStartDate);
    dayDate.setDate(dayDate.getDate() + (selectedDay - 1));
    const match = localPicksDestinations.find((d: any) => {
      const s = coerceDate(d.startDate);
      const e = coerceDate(d.endDate) ?? s;
      if (!s || !e) return false;
      return dayDate >= s && dayDate <= e;
    });
    if (match?.city) return match;
  }

  // Otherwise, show picks for all cities in a multi-city trip (combined list).
  return null;
}, [selectedDay, localPicksDestinations, safeTripStartDate]);

const normalizeText = (text?: string) =>
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const distanceMeters = (a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) => {
  const R = 6371000;
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180;
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180;
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;
  const sin1 = Math.sin(dLat / 2);
  const sin2 = Math.sin(dLon / 2);
  const h = sin1 * sin1 + Math.cos(lat1) * Math.cos(lat2) * sin2 * sin2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

const filterOutExistingItineraryItems = useCallback(
  (recommendations: LocalPicksRecommendationItem[]) => {
    if (!recommendations?.length) return recommendations;

    const titleKeys = new Set<string>();
    const locKeys = new Set<string>();
    const coords: Array<{ latitude: number; longitude: number }> = [];

    for (const item of itineraryItems || []) {
      titleKeys.add(normalizeText(item.title));
      if (item.location) locKeys.add(normalizeText(item.location));
      if (item.address) locKeys.add(normalizeText(item.address));
      const c = (item as any)?.coordinates as LocationCoordinates | undefined;
      if (c?.latitude && c?.longitude) {
        coords.push({ latitude: c.latitude, longitude: c.longitude });
      }
    }

    return recommendations.filter((rec) => {
      const nameKey = normalizeText(rec.name);
      if (titleKeys.has(nameKey)) return false;
      if (rec.location?.address && locKeys.has(normalizeText(rec.location.address))) return false;

      if (rec.location?.latitude && rec.location?.longitude && coords.length) {
        const r = { latitude: rec.location.latitude, longitude: rec.location.longitude };
        if (coords.some((c) => distanceMeters(c, r) <= 150)) return false;
      }

      return true;
    });
  },
  [itineraryItems],
);

// Calculate current location from itinerary items or trip destination
const currentLocation = useMemo<LocationCoordinates | undefined>(() => {
  // Try to get location from most recent itinerary item
  if (itineraryItems.length > 0) {
    const recentItem = itineraryItems
      .filter((item) => (item as any)?.coordinates)
      .sort((a, b) => {
        const dateA = coerceDate(a.startDate)?.getTime() ?? 0;
        const dateB = coerceDate(b.startDate)?.getTime() ?? 0;
        return dateB - dateA;
      })[0];

    const coords = (recentItem as any)?.coordinates as LocationCoordinates | undefined;
    if (coords) {
      return coords;
    }
  }

  if (trip.destinations?.[0]?.coordinates) {
    return trip.destinations[0].coordinates;
  }

  return undefined;
}, [itineraryItems, trip.destinations]);

// NOTE (web performance): "For you" recommendations require multiple network calls (trending + similar trips + itinerary fetches).
// Preloading even a count can make the Plan screen feel frozen on web during dev. We only load "For you" once the Ideas modal
// is opened and the user visits the For you tab (handled inside PlanningIdeasModal).
useEffect(() => {
  setForYouCount(0);
}, [isArchived, trip.id]);

// Prefetch attachments for upcoming items (offline window handled in helper)
useEffect(() => {
  const prefetch = async () => {
    try {
      const normalized = itineraryItems.map((it) => ({
        startDate: coerceDate(it.startDate) ?? new Date(),
        attachments: normalizeAttachments(it.attachments, it.documents || []),
      }));
      await prefetchUpcomingAttachments(normalized);
    } catch (error) {
      logger.warn(
        '[PlanningTab] Attachment prefetch failed',
        undefined,
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  };

  prefetch();
}, [itineraryItems]);

// Calculate selected day from itinerary items
useEffect(() => {
  if (itineraryItems.length > 0 && safeTripStartDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(safeTripStartDate);
    startDate.setHours(0, 0, 0, 0);
    const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceStart >= 0) {
      setSelectedDay(daysSinceStart + 1);
    }
  }
}, [itineraryItems, safeTripStartDate]);

// Check if trip is currently active (between start and end dates)
const isTripActive = useMemo(() => {
  if (!safeTripStartDate || !safeTripEndDate) {
    return false;
  }
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const startDate = new Date(safeTripStartDate);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(safeTripEndDate);
  endDate.setHours(0, 0, 0, 0);
  return now >= startDate && now <= endDate;
}, [safeTripStartDate, safeTripEndDate]);

const isTripUpcoming = useMemo(() => {
  if (!safeTripStartDate) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const startDate = new Date(safeTripStartDate);
  startDate.setHours(0, 0, 0, 0);
  return now < startDate;
}, [safeTripStartDate]);

const hasFlightBooking = useMemo(() => bookings?.some((b) => b.type === 'flight'), [bookings]);

const visaCheck: VisaCheckResult = useMemo(() => {
  try {
    const citizenship = profile?.citizenship || profile?.homeCountry;
    if (!primaryDestination || !citizenship) {
      return null;
    }

    return checkVisaRequirement(primaryDestination, citizenship, trip.travelingFrom);
  } catch (error) {
    logger.error('[PlanningTab] Error in visaCheck useMemo', error instanceof Error ? error : new Error(String(error)));
    return null;
  }
}, [primaryDestination, trip.travelingFrom, profile?.citizenship, profile?.homeCountry]);

// TravelBuddy-backed visa requirements modal (replaces Google redirect in readiness)
const passportCountryForVisaCall =
  profile?.citizenshipIso2 ||
  profile?.homeCountryIso2 ||
  profile?.citizenship ||
  profile?.homeCountry ||
  undefined;
const passportCountryForVisaLabel =
  profile?.citizenship ||
  profile?.homeCountry ||
  passportCountryForVisaCall;
const [visaModalVisible, setVisaModalVisible] = useState(false);
const [visaModalLoading, setVisaModalLoading] = useState(false);
const [visaModalError, setVisaModalError] = useState<string | null>(null);
const [visaRequirement, setVisaRequirement] = useState<VisaRequirement | null>(null);
const lastVisaFetchKeyRef = useRef<string | null>(null);
const [destinationCountryIso2, setDestinationCountryIso2] = useState<string>('');

const needsDestinationCountryConfirm = useMemo(() => {
  const structured =
    trip.destinations?.[0]?.countryCode ||
    toIso2CountryCode(trip.destinations?.[0]?.country || '');
  if (structured) return false;
  const raw = (trip.destination || trip.destinations?.[0]?.city || '').toString();
  if (!raw) return true;
  const countryPart = raw.split(',').map((p) => p.trim()).pop() || raw;
  return !toIso2CountryCode(countryPart);
}, [trip.destination, trip.destinations]);

const countryOptions: SelectOption[] = useMemo(
  () => COUNTRY_ISO2_OPTIONS.map((o) => ({ value: o.value, label: o.label, meta: o.meta })),
  [],
);

useEffect(() => {
  if (!visaModalVisible) return;
  const existing =
    (trip.destinations?.[0]?.countryCode ? toIso2CountryCode(trip.destinations[0].countryCode) : undefined) ||
    (trip.destinations?.[0]?.country ? toIso2CountryCode(trip.destinations[0].country) : undefined) ||
    undefined;
  if (existing && !destinationCountryIso2) {
    setDestinationCountryIso2(existing);
  }
}, [visaModalVisible, trip.destinations, destinationCountryIso2]);

const persistDestinationCountryAndRetry = useCallback(async () => {
  if (!destinationCountryIso2) {
    Alert.alert('Destination country required', 'Please select the destination country to continue.');
    return;
  }
  if (!passportCountryForVisaCall) {
    Alert.alert(
      'Add your citizenship',
      'To check visa requirements, please add your citizenship in your profile.',
    );
    return;
  }

  const countryName = fromIso2CountryCode(destinationCountryIso2) || destinationCountryIso2;
  const baseCityLabel = (trip.destination || trip.destinations?.[0]?.city || trip.name || '').toString();

  const nextDestinations: TripDestination[] =
    trip.destinations && trip.destinations.length > 0
      ? [
        {
          ...trip.destinations[0],
          country: countryName,
          countryCode: destinationCountryIso2,
        },
        ...trip.destinations.slice(1),
      ]
      : [
        {
          city: baseCityLabel || `${trip.name}, ${countryName}`,
          country: countryName,
          countryCode: destinationCountryIso2,
          startDate: safeTripStartDate ?? new Date(),
          endDate: safeTripEndDate ?? safeTripStartDate ?? new Date(),
        },
      ];

  const legacyDestination =
    trip.destination && !trip.destination.includes(',')
      ? `${trip.destination}, ${countryName}`
      : trip.destination;

  setVisaModalLoading(true);
  setVisaModalError(null);
  try {
    await updateTrip(trip.id, userId, {
      destinations: nextDestinations,
      destination: legacyDestination,
    });

    const patchedTrip = { ...trip, destinations: nextDestinations, destination: legacyDestination };
    const result = await getTravelRequirements(patchedTrip, passportCountryForVisaCall);
    if (!result) {
      setVisaRequirement(null);
      setVisaModalError('Unable to fetch visa requirements right now.');
      return;
    }

    const visaReq: VisaRequirement = {
      status: (result.visaStatus || 'unknown') as VisaRequirement['status'],
      visaRequired: !!result.visaRequired,
      stayDuration: result.stayDuration,
      notes: result.visaNotes,
      eVisaLink: result.eVisaLink,
      embassyLink: result.embassyLink,
      lastUpdated: result.lastUpdated,
      destination: countryName,
      passportCountry: passportCountryForVisaLabel || passportCountryForVisaCall,
    };
    setVisaRequirement(visaReq);
    lastVisaFetchKeyRef.current = `${trip.id}:${baseCityLabel}:${passportCountryForVisaCall}`;
  } catch (error) {
    logger.error(
      'Error saving destination country for visa check',
      error instanceof Error ? error : new Error(String(error)),
    );
    setVisaModalError('Unable to save destination country. Please try again.');
  } finally {
    setVisaModalLoading(false);
  }
}, [
  destinationCountryIso2,
  passportCountryForVisaCall,
  passportCountryForVisaLabel,
  trip,
  userId,
]);

// Local picks cache: keep results warm so the Ideas modal opens quickly on device.
// On web we still defer loading until the modal opens (to avoid heavy work / slow networks).
const localPicksCacheRef = useRef<Map<string, { savedAt: number; items: LocalPicksRecommendationItem[] }>>(
  new Map(),
);
const localPicksInFlightRef = useRef<Map<string, Promise<void>>>(new Map());
const lastLocalPicksRefreshTokenRef = useRef<number>(0);
const LOCAL_PICKS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const LOCAL_PICKS_CACHE_VERSION = 'v2-geo';

const localPicksCacheKey = useMemo(() => {
  const targetCity =
    selectedCityForLocalPicks?.city ||
    (Array.isArray(localPicksDestinations) && localPicksDestinations[0]?.city) ||
    primaryDestination ||
    '';
  return `${LOCAL_PICKS_CACHE_VERSION}:${trip.id}:${targetCity}`.toLowerCase();
}, [trip.id, primaryDestination, selectedCityForLocalPicks?.city, localPicksDestinations]);

useEffect(() => {
  const shouldPrefetch =
    !isArchived &&
    !!primaryDestination &&
    (Platform.OS === 'web' ? ideasModalVisible : true);
  if (!shouldPrefetch) {
    // Keep any cached results in state for snappy modal reopen; just stop loading indicators.
    setLocalPicksLoading(false);
    return;
  }

  let cancelled = false;

  const maybeServeCache = (): boolean => {
    const cached = localPicksCacheRef.current.get(localPicksCacheKey);
    if (cached && Date.now() - cached.savedAt < LOCAL_PICKS_CACHE_TTL_MS) {
      setLocalPicksError(null);
      setLocalPicksRecommendations(cached.items);
      return true;
    }
    return false;
  };

  const fetchRecommendations = async (opts?: { force?: boolean }) => {
    if (cancelled) return;
    const force = opts?.force === true;

    const hasFreshCache = maybeServeCache();
    if (hasFreshCache && !force) {
      return;
    }

    const existing = localPicksInFlightRef.current.get(localPicksCacheKey);
    if (existing) {
      await existing;
      return;
    }

    const run = (async () => {
      setLocalPicksLoading(true);
      setLocalPicksError(null);

      try {
        const destinationsToFetch =
          selectedCityForLocalPicks?.city
            ? [selectedCityForLocalPicks]
            : localPicksDestinations;

        // Multi-segment trips: never reuse one shared lat/lng for every city — that runs the same
        // nearby search for each label and duplicates POIs with mismatched city chips. Use per-segment
        // coordinates only, otherwise the cloud function falls back to text search for that city name.
        const multiSegment = destinationsToFetch.length > 1;

        const results = await Promise.all(
          destinationsToFetch.map((dest: any) => {
            const hasDestCoords =
              typeof dest.coordinates?.latitude === 'number' &&
              typeof dest.coordinates?.longitude === 'number';
            let latitude: number | undefined;
            let longitude: number | undefined;
            if (hasDestCoords) {
              latitude = dest.coordinates.latitude;
              longitude = dest.coordinates.longitude;
            } else if (!multiSegment) {
              latitude = primaryDestinationCoordinates?.latitude;
              longitude = primaryDestinationCoordinates?.longitude;
            }
            const coords =
              typeof latitude === 'number' && typeof longitude === 'number'
                ? { latitude, longitude }
                : {};

            return requestCityPicks({
              city: dest.city,
              ...coords,
              ttlHours: 24,
            }).then((res) => ({
              city: dest.city,
              items: res.items || [],
            }));
          }),
        );

        const merged = dedupeLocalPicksByPlaceId(
          results.flatMap((r) => mapCityPicksToRecommendations(r.items, r.city)),
        );
        const filtered = filterOutExistingItineraryItems(merged);

        if (!cancelled) {
          localPicksCacheRef.current.set(localPicksCacheKey, { savedAt: Date.now(), items: filtered });
          setLocalPicksRecommendations(filtered);
        }
      } catch (error: any) {
        if (!cancelled) {
          logger.warn(
            '[PlanningTab] Failed to load local picks',
            undefined,
            error instanceof Error ? error : new Error(String(error)),
          );
          const errorMessage = error?.message || 'Unable to load local picks right now.';
          const errorCode = error?.code;
          const isConfigError =
            errorCode === 'functions/failed-precondition' ||
            /not configured|api key|failed-precondition/i.test(errorMessage);

          setLocalPicksError(
            isConfigError
              ? 'Local picks service is not configured for this environment. Please contact support.'
              : errorMessage,
          );
          setLocalPicksRecommendations([]);
        }
      } finally {
        if (!cancelled) {
          setLocalPicksLoading(false);
        }
      }
    })();

    localPicksInFlightRef.current.set(localPicksCacheKey, run);
    try {
      await run;
    } finally {
      if (localPicksInFlightRef.current.get(localPicksCacheKey) === run) {
        localPicksInFlightRef.current.delete(localPicksCacheKey);
      }
    }
  };

  // Defer background prefetch on native so the Plan screen stays responsive; when the Ideas modal is open, fetch immediately.
  const delayMs = Platform.OS === 'web' ? 0 : ideasModalVisible ? 0 : 300;
  const forceRefresh = localPicksRefreshToken !== lastLocalPicksRefreshTokenRef.current;
  const timer = setTimeout(() => {
    lastLocalPicksRefreshTokenRef.current = localPicksRefreshToken;
    fetchRecommendations({ force: forceRefresh });
  }, delayMs);

  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
}, [
  isArchived,
  primaryDestination,
  ideasModalVisible,
  trip.id,
  localPicksRefreshToken,
  localPicksCacheKey,
  localPicksDestinations,
  selectedCityForLocalPicks,
  filterOutExistingItineraryItems,
  primaryDestinationCoordinates?.latitude,
  primaryDestinationCoordinates?.longitude,
]);

const openVisaFallbackSearch = useCallback(async () => {
  if (!primaryDestination) return;
  const url = visaCheck?.embassyUrl;
  try {
    if (!url) {
      Alert.alert(
        'Visa requirements',
        'We couldn’t find an official visa link for this destination. Open Visa Requirements to see the in-app checklist, or check the destination country’s official immigration website.',
      );
      return;
    }
    const canOpen = await Linking.canOpenURL(url);
    if (canOpen) {
      await Linking.openURL(url);
    } else {
      Alert.alert('Error', 'Unable to open the link. Please check your internet connection.');
    }
  } catch (error) {
    logger.error('Error opening visa link', error instanceof Error ? error : new Error(String(error)));
    Alert.alert('Error', 'Unable to open the link. Please try again.');
  }
}, [primaryDestination, visaCheck?.embassyUrl]);

const handleVisaLinkPress = useCallback(async () => {
  // Open in-app visa requirements instead of sending users to Google.
  if (!primaryDestination) return;

  if (!passportCountryForVisaCall) {
    Alert.alert(
      'Add your citizenship',
      'To check visa requirements, please add your citizenship in your profile.',
    );
    return;
  }

  setVisaModalVisible(true);
  setVisaModalError(null);

  const fetchKey = `${trip.id}:${primaryDestination}:${passportCountryForVisaCall}`;
  if (lastVisaFetchKeyRef.current === fetchKey && visaRequirement) {
    return;
  }

  setVisaModalLoading(true);
  try {
    const result = await getTravelRequirements(trip, passportCountryForVisaCall);
    if (!result) {
      setVisaRequirement(null);
      setVisaModalError(
        'Please update your trip destination to include the country (e.g., "Tokyo, Japan") and try again.',
      );
      return;
    }

    // Convert TravelRequirements -> VisaRequirement for existing VisaChecklist UI.
    const destination = (trip.destination || trip.destinations?.[0]?.city || primaryDestination || '').toString();
    const destinationCountry = destination.split(',').map((p) => p.trim()).pop() || destination;
    const visaReq: VisaRequirement = {
      status: (result.visaStatus || 'unknown') as VisaRequirement['status'],
      visaRequired: !!result.visaRequired,
      stayDuration: result.stayDuration,
      notes: result.visaNotes,
      eVisaLink: result.eVisaLink,
      embassyLink: result.embassyLink,
      lastUpdated: result.lastUpdated,
      destination: destinationCountry,
      passportCountry: passportCountryForVisaLabel || passportCountryForVisaCall,
    };

    setVisaRequirement(visaReq);
    lastVisaFetchKeyRef.current = fetchKey;
  } catch (error) {
    logger.error('Error fetching visa requirements', error instanceof Error ? error : new Error(String(error)));
    setVisaRequirement(null);
    setVisaModalError('Unable to fetch visa requirements right now.');
  } finally {
    setVisaModalLoading(false);
  }
}, [
  primaryDestination,
  passportCountryForVisaCall,
  passportCountryForVisaLabel,
  trip,
  visaRequirement,
  lastVisaFetchKeyRef,
]);

const handleOpenIdeas = useCallback((preferredTab?: LegacyIdeasTab, targetDate?: Date) => {
  // Default to Local Picks to avoid unnecessary AI calls.
  setIdeasTab(normalizeIdeasTab(preferredTab));
  if (targetDate && safeTripStartDate) {
    const start = new Date(safeTripStartDate);
    start.setHours(0, 0, 0, 0);
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((target.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    setIdeasTargetDay(Math.max(1, diffDays + 1));
  } else {
    setIdeasTargetDay(undefined);
  }
  setIdeasModalVisible(true);
}, [safeTripStartDate, bottomSheetMaxHeight]);

const handleCloseIdeas = useCallback(() => {
  setIdeasModalVisible(false);
  setIdeasTargetDay(undefined);
}, []);
const handleMinimizeIdeas = useCallback(() => {
  // Minimize keeps existing suggestion context so the parent can reopen quickly.
  setIdeasModalVisible(false);
}, []);
const handleRefreshLocalPicks = useCallback(() => setLocalPicksRefreshToken((token) => token + 1), []);
const handleOpenFullMap = useCallback(() => {
  if (!trip?.id) {
    return;
  }
  router.push({
    pathname: '/(tabs)/travel',
    params: { tab: 'maps', tripId: trip.id },
  });
}, [router, trip?.id]);

logger.debug('[PlanningTab] About to render JSX');

try {
  logger.debug('[PlanningTab] Inside try block, about to return JSX');
  logger.debug('[PlanningTab] Creating main View container');
  const jsx = (
    <View
      style={{
        paddingBottom: theme.spacing(4),
        gap: theme.spacing(3),
      }}
    >
      {/* Status strip (minimal; hero header lives on the parent screen) */}
      {!isArchived && showTripOverviewCard && (
        <ComponentErrorBoundary name="TripOverview" fallback={null}>
          <TripOverviewCard
            trip={trip}
            userId={userId}
            visaCheck={visaCheck}
            onVisaLinkPress={handleVisaLinkPress}
            citizenshipLabel={profile?.citizenship || profile?.homeCountry}
            onOpenFullMap={handleOpenFullMap}
            itineraryItems={itineraryItems}
            bookings={safeBookings}
            readinessAlerts={readinessAlerts}
            onOpenAlerts={onOpenAlerts}
            onAlertAction={onAlertAction}
            readinessCollapsed={readinessCollapsed}
            onToggleReadiness={onToggleReadiness}
            onOpenOfflineDownload={onOpenOfflineDownload}
          />
        </ComponentErrorBoundary>
      )}

      {/* Schedule (Planning actions merged into ItineraryView header) */}
      <ComponentErrorBoundary name="ItineraryView">
        {itineraryLoading ? (
          <Card style={{ marginTop: theme.spacing(2), padding: theme.spacing(4), alignItems: 'center' }}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <ThemedText
              variant="body"
              color={theme.colors.textSecondary}
              style={{ marginTop: theme.spacing(2) }}
            >
              Loading itinerary...
            </ThemedText>
          </Card>
        ) : itineraryItems.length === 0 ? (
          <Card style={{ gap: theme.spacing(1.5), padding: theme.spacing(2) }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 17, fontWeight: '700' }}>
              Start planning this trip
            </ThemedText>
            <Body color={theme.colors.textSecondary}>
              Create a quick draft itinerary or add your first item. You can refine and reorder anytime.
            </Body>
            <View style={{ flexDirection: 'row', gap: theme.spacing(1), flexWrap: 'wrap' }}>
              {onGenerateSuggestions && (
                <Button
                  label={aiSuggestionsLoading ? 'Generating…' : 'Generate a draft plan'}
                  onPress={() => {
                    if (localPicksRecommendations.length > 0) {
                      handleOpenIdeas('localPicks');
                      return;
                    }
                    // Open the Ideas modal immediately so users can see progress + results as they stream in.
                    handleOpenIdeas('ai');
                    onGenerateSuggestions('itinerary');
                  }}
                  loading={aiSuggestionsLoading}
                  disabled={!canEdit}
                  style={{ flex: 1, minWidth: 200 }}
                  accessibilityLabel="Generate a draft itinerary with AI suggestions"
                  accessibilityHint="Creates a starter plan you can review and edit"
                />
              )}
              <Button
                label="Add first item"
                variant="secondary"
                onPress={() => onAddItem()}
                disabled={!canEdit}
                style={{ flex: 1, minWidth: 160 }}
                accessibilityLabel="Add the first itinerary item"
                accessibilityHint="Opens the form to add an activity or booking"
              />
            </View>
            <Caption color={theme.colors.textSecondary}>
              Flow: Generate → Review → Add/Adjust → Share with companions.
            </Caption>
          </Card>
        ) : (
          <ItineraryView
            trip={trip}
            items={itineraryItems}
            refreshPlanItineraryFromServer={refreshPlanItineraryFromServer}
            bookings={safeBookings}
            onAddItem={onAddItem}
            onOpenImport={() => setImportVisible(true)}
            onEditItem={onEditItem}
            onDeleteItem={onDeleteItem}
            onViewDocument={onViewDocument}
            onOpenIdeas={handleOpenIdeas}
            canEdit={canEdit}
            isArchived={isArchived}
          />
        )}
      </ComponentErrorBoundary>

      {/* Trip tools (formerly “More options”) */}
      {!isArchived && (
        <Card variant="outlined" padding={theme.spacing(2)} style={{ marginBottom: theme.spacing(2) }}>
          <View style={{ gap: theme.spacing(1.25) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                <Ionicons name="apps-outline" size={18} color={theme.colors.primary} />
                <ThemedText variant="h3" style={{ fontSize: 16, fontWeight: '800' }}>
                  Trip hub
                </ThemedText>
              </View>
              <Caption color={theme.colors.textSecondary}>Shortcuts & extras</Caption>
            </View>

            <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '40' }} />

            {(onSearchFlights || onSearchHotels) && (
              <ComponentErrorBoundary name="TripHubBookBlock" fallback={null}>
                <ToolsRow
                  icon="airplane-outline"
                  title="Book"
                  subtitle="Flights and hotels"
                  expanded={tripToolsExpandedKey === 'book'}
                  onPress={() => toggleTripTool('book')}
                  themeColor={theme.colors.primary}
                />
                {tripToolsExpandedKey === 'book' && (
                  <View style={{ marginTop: theme.spacing(0.5), gap: theme.spacing(1) }}>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      {onSearchFlights && (
                        <Button
                          label="Flights"
                          variant="secondary"
                          onPress={onSearchFlights}
                          disabled={!canEdit}
                          icon={<Ionicons name="airplane-outline" size={18} color={theme.colors.text} />}
                          style={{ flex: 1 }}
                          accessibilityLabel="Book flights"
                          accessibilityHint="Search and book flights for this trip"
                        />
                      )}
                      {onSearchHotels && (
                        <Button
                          label="Hotels"
                          variant="secondary"
                          onPress={onSearchHotels}
                          disabled={!canEdit}
                          icon={<Ionicons name="bed-outline" size={18} color={theme.colors.text} />}
                          style={{ flex: 1 }}
                          accessibilityLabel="Book hotels"
                          accessibilityHint="Search and book hotels for this trip"
                        />
                      )}
                    </View>
                    {!canEdit && (
                      <Body color={theme.colors.textSecondary}>
                        You don’t have permission to add bookings to this trip.
                      </Body>
                    )}
                  </View>
                )}

                <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '35' }} />
              </ComponentErrorBoundary>
            )}

            <ToolsRow
              icon="checkbox-outline"
              title="Checklist & packing"
              subtitle="Docs, reminders, and must-dos"
              expanded={tripToolsExpandedKey === 'checklist'}
              onPress={() => toggleTripTool('checklist')}
              themeColor={theme.colors.primary}
            />
            {tripToolsExpandedKey === 'checklist' && (
              <View style={{ marginTop: theme.spacing(0.5) }}>
                <ComponentErrorBoundary name="SmartRemindersPanel" fallback={null}>
                  <SmartRemindersPanel
                    trip={trip}
                    itineraryItems={itineraryItems}
                    collaboratorCount={trip.collaborators?.length || 0}
                    onOpenIdeas={handleOpenIdeas}
                  />
                </ComponentErrorBoundary>
                <ComponentErrorBoundary name="TripChecklist" fallback={null}>
                  <TripChecklist
                    trip={trip}
                    bookings={safeBookings}
                    userId={userId}
                    passportCountry={profile?.citizenshipIso2 || profile?.citizenship || profile?.homeCountryIso2 || profile?.homeCountry}
                    onViewDocument={onViewDocument}
                  />
                </ComponentErrorBoundary>
              </View>
            )}

            <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '35' }} />

            <ToolsRow
              icon="help-circle-outline"
              title="Maybe"
              subtitle="Vote on ideas before you commit"
              badgeCount={maybeIdeasCount}
              expanded={tripToolsExpandedKey === 'maybe'}
              onPress={() => toggleTripTool('maybe')}
              themeColor={theme.colors.primary}
            />
            {tripToolsExpandedKey === 'maybe' && (
              <View style={{ marginTop: theme.spacing(0.5) }}>
                <ComponentErrorBoundary name="MaybeContainer" fallback={null}>
                  <MaybeContainer
                    tripId={trip.id}
                    trip={trip}
                    canEdit={canEdit}
                    refreshToken={maybeRefreshToken}
                    onOpenIdeas={() => handleOpenIdeas()}
                    onScheduleItem={async (maybe: MaybeItem) => {
                      // Dismiss Ideas modal first so Schedule Maybe modal can present (avoids iOS "already presenting").
                      handleCloseIdeas();
                      // Wait for dismiss + interactions to settle before presenting another modal.
                      const waitMs = ideasModalVisible ? 400 : 50;
                      await new Promise((r) => setTimeout(r, waitMs));
                      await new Promise<void>((r) => {
                        InteractionManager.runAfterInteractions(() => {
                          requestAnimationFrame(() => r());
                        });
                      });

                      // Preferred flow: prompt for date/time (Plan screen owns the itinerary add flow)
                      if (onScheduleMaybeItem) {
                        return await onScheduleMaybeItem(maybe);
                      }

                      // Fallback: schedule into itinerary as a simple placeholder.
                      if (!onAddRecommendationToItinerary) return false;
                      await onAddRecommendationToItinerary({
                        title: maybe.title,
                        description: maybe.description,
                        category: 'activity',
                        startDate: trip.startDate,
                        startTime: 'flexible',
                        address: undefined,
                        location: undefined,
                      });
                      return true;
                    }}
                  />
                </ComponentErrorBoundary>
              </View>
            )}

            <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '35' }} />

            <ComponentErrorBoundary name="IdeasPreviewRow" fallback={null}>
              <CompactIdeasRow
                totalIdeas={(aiSuggestions?.suggestions?.length || 0) + localPicksRecommendations.length + forYouCount}
                onOpenIdeas={handleOpenIdeas}
                aiSuggestionsLoading={aiSuggestionsLoading}
              />
            </ComponentErrorBoundary>

            <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '35' }} />

            <ToolsRow
              icon="sparkles-outline"
              title="Assist & Copilot"
              subtitle="Rebooking, disruptions, and budget help"
              expanded={tripToolsExpandedKey === 'assist'}
              onPress={() => toggleTripTool('assist')}
              themeColor={theme.colors.primary}
            />
            {tripToolsExpandedKey === 'assist' && (
              <View style={{ marginTop: theme.spacing(0.5), gap: theme.spacing(1.25) }}>
                <ComponentErrorBoundary name="Assist" fallback={null}>
                  {(onLaunchRebooking || onLaunchDisruption || onLaunchBudget) && (
                    <CopilotQuickActions
                      onLaunchRebooking={hasFlightBooking ? onLaunchRebooking : undefined}
                      onLaunchDisruption={(isTripActive || isTripUpcoming) ? onLaunchDisruption : undefined}
                      onLaunchBudget={trip.budget ? onLaunchBudget : undefined}
                      loading={copilotLoading}
                    />
                  )}
                  <PlaybookSection
                    onMessageCollaborators={(message) => {
                      // Lightweight: use the native share sheet for now (no push / no in-app DM)
                      // so the user can send it to the group chat they already use.
                      try {
                        // eslint-disable-next-line @typescript-eslint/no-var-requires
                        const Share = require('react-native').Share as typeof import('react-native').Share;
                        Share.share({ message }).catch(() => { });
                      } catch {
                        // ignore
                      }
                    }}
                  />
                  {isTripActive && onOpenTravelCompanion && (
                    <Button
                      label="Open travel companion"
                      variant="secondary"
                      size="small"
                      onPress={handleOpenTravelCompanion}
                      icon={<Ionicons name="compass-outline" size={16} color={theme.colors.text} />}
                    />
                  )}
                </ComponentErrorBoundary>
              </View>
            )}

            <View style={{ height: 1, backgroundColor: theme.colors.borderMuted + '35' }} />

            <ToolsRow
              icon="briefcase-outline"
              title="Travel services"
              subtitle="Book, reserve, and add-ons"
              expanded={tripToolsExpandedKey === 'services'}
              onPress={() => toggleTripTool('services')}
              themeColor={theme.colors.primary}
            />
            {tripToolsExpandedKey === 'services' && (
              <View style={{ marginTop: theme.spacing(0.5) }}>
                <ComponentErrorBoundary name="PartnerMarketplace" fallback={null}>
                  <PartnerMarketplace trip={trip} />
                </ComponentErrorBoundary>
              </View>
            )}

          </View>
        </Card>
      )}

      {/* Modals */}
      {!isArchived && (
        <ComponentErrorBoundary name="ImportSheet" fallback={null}>
          <ImportSheet
            visible={importVisible}
            onClose={() => setImportVisible(false)}
            tripName={trip.name}
            canEdit={canEdit}
            onImportBookings={onOpenEmailProvider}
            onImportDocuments={onSmartUpload}
          />
        </ComponentErrorBoundary>
      )}
      {!isArchived && (
        <ComponentErrorBoundary name="PlanningIdeasModal" fallback={null}>
        <PlanningIdeasModal
          visible={ideasModalVisible}
          onClose={handleCloseIdeas}
          activeTab={ideasTab}
          onTabChange={setIdeasTab}
          currentDay={ideasTargetDay ?? selectedDay}
          currentLocation={currentLocation}
          aiSuggestions={aiSuggestions}
          aiSuggestionsLoading={aiSuggestionsLoading}
          aiSuggestionsError={aiSuggestionsError}
          aiSuggestionsErrorMode={aiSuggestionsErrorMode}
          aiSuggestionsLoadingExtended={aiSuggestionsLoadingExtended}
          aiSuggestionsRequestMode={aiSuggestionsRequestMode}
          onGenerateSuggestions={onGenerateSuggestions}
          onCancelGenerateSuggestions={onCancelGenerateSuggestions}
          onMinimizeIdeas={handleMinimizeIdeas}
          onApplyAllSuggestions={onApplyAllSuggestions}
          applyingSuggestions={applyingSuggestions}
          onAddSingleSuggestion={onAddSingleSuggestion}
          addingSuggestionIds={suggestionIds}
          EnhancedSuggestionCard={EnhancedSuggestionCard}
          mapToEnhancedSuggestion={mapToEnhancedSuggestion}
          trip={tripWithOwner}
          itineraryItems={itineraryItems}
          localPicksRecommendations={localPicksRecommendations}
          localPicksLoading={localPicksLoading}
          localPicksError={localPicksError}
          onRefreshLocalPicks={handleRefreshLocalPicks}
          onAddRecommendationToItinerary={onAddRecommendationToItinerary}
          onAfterApplyAll={handleCloseIdeas}
          onMaybeAdded={() => setMaybeRefreshToken((t) => t + 1)}
          userId={userId}
          aiPlannerIntentAppendixRef={aiPlannerIntentAppendixRef}
          planIdeaCardsQuota={planIdeaCardsQuota}
        />
        </ComponentErrorBoundary>
      )}

      {/* Visa requirements (in-app; powered by TravelBuddy API via Functions) */}
      <Modal
        visible={visaModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setVisaModalVisible(false)}
      >
        <ComponentErrorBoundary name="VisaModal" fallback={null}>
        <SafeAreaView
          style={{
            flex: 1,
            backgroundColor: theme.colors.background,
          }}
          edges={['top', 'bottom', 'left', 'right']}
        >
          <View
            style={{
              flex: 1,
              paddingTop:
                theme.spacing(2) + Math.max(0, effectiveTopInset(insets.top) - insets.top),
              paddingHorizontal: theme.spacing(2),
              paddingBottom: theme.spacing(2),
            }}
          >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <ThemedText variant="h2" style={{ fontSize: 18, fontWeight: '800' }}>
              Visa requirements
            </ThemedText>
            <Pressable
              onPress={() => setVisaModalVisible(false)}
              style={({ pressed }) => ({
                padding: theme.spacing(1),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.6 : 1,
              })}
            >
              <Ionicons name="close" size={20} color={theme.colors.text} />
            </Pressable>
          </View>

          <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
            For your trip to {trip.destination || trip.destinations?.[0]?.city || 'your destination'}
          </Caption>

          <ScrollView
            style={{ marginTop: theme.spacing(2) }}
            contentContainerStyle={{ paddingBottom: theme.spacing(3), gap: theme.spacing(2) }}
          >
            {visaModalLoading ? (
              <Card variant="outlined" style={{ padding: theme.spacing(2), alignItems: 'center' }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
                  Checking visa requirements…
                </Caption>
              </Card>
            ) : visaRequirement ? (
              <VisaChecklist requirement={visaRequirement} trip={trip} />
            ) : (
              <Card variant="outlined" style={{ padding: theme.spacing(2) }}>
                <Body color={theme.colors.textSecondary}>
                  {visaModalError || 'Visa requirements are temporarily unavailable.'}
                </Body>
                {needsDestinationCountryConfirm && (
                  <View style={{ marginTop: theme.spacing(1.5), gap: theme.spacing(1.25) }}>
                    <Body color={theme.colors.text}>
                      To check visa requirements, confirm the destination country for this trip.
                    </Body>
                    <SelectField
                      label="Destination country"
                      placeholder="Select country"
                      value={destinationCountryIso2}
                      options={countryOptions}
                      onValueChange={setDestinationCountryIso2}
                      helperText="This helps us determine visa requirements accurately."
                    />
                    <Button
                      label="Save country & check"
                      onPress={persistDestinationCountryAndRetry}
                    />
                  </View>
                )}
                <View style={{ marginTop: theme.spacing(1.5) }}>
                  <Button
                    label={needsDestinationCountryConfirm ? 'Find official visa info' : 'Try again'}
                    variant="secondary"
                    onPress={needsDestinationCountryConfirm ? openVisaFallbackSearch : handleVisaLinkPress}
                  />
                </View>
              </Card>
            )}
          </ScrollView>
        </View>
        </SafeAreaView>
        </ComponentErrorBoundary>
      </Modal>

    </View>
  );
  logger.debug('[PlanningTab] JSX created successfully, returning');
  return jsx;
} catch (error) {
  logger.error('[PlanningTab] Error during render', error instanceof Error ? error : new Error(String(error)), {
    stack: error instanceof Error ? error.stack : 'No stack',
  });
  return (
    <View style={{ padding: theme.spacing(4), alignItems: 'center' }}>
      <ThemedText variant="bodyStrong" color={theme.colors.error}>
        Error rendering planning tab. Please try refreshing.
      </ThemedText>
      <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
        {error instanceof Error ? error.message : 'Unknown error'}
      </Body>
    </View>
  );
}
}

export function PlanningTab({
  trip,
  itineraryItems,
  itineraryLoading = false,
  bookings,
  isArchived,
  canEdit,
  userId,
  userPermission,
  isOwner,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onViewDocument,
  onOpenEmailProvider,
  inboxSyncing,
  inboxConnected,
  inboxProviderLabel,
  onLaunchRebooking,
  onLaunchDisruption,
  onLaunchBudget,
  copilotLoading,
  aiSuggestions,
  aiSuggestionsLoading,
  aiSuggestionsError,
  aiSuggestionsErrorMode,
  aiSuggestionsLoadingExtended,
  aiSuggestionsRequestMode,
  onGenerateSuggestions,
  onCancelGenerateSuggestions,
  onAddRecommendationToItinerary,
  onApplyAllSuggestions,
  applyingSuggestions,
  onAddSingleSuggestion,
  addingSuggestionIds,
  EnhancedSuggestionCard,
  mapToEnhancedSuggestion,
  profile,
  onSearchFlights,
  onSearchHotels,
  onSmartUpload,
  smartUploadLoading,
  onOpenTravelCompanion,
  onOpenOfflineDownload,
  readinessAlerts,
  onOpenAlerts,
  onAlertAction,
  readinessCollapsed,
  onToggleReadiness,
  showTripOverviewCard = true,
  openCollaboratorSuggestToken,
  openCommentsToken,
  onScheduleMaybeItem,
  onIdeasModalVisibilityChange,
  ideasModalReopenToken,
  onTripOfflineCacheChanged,
  aiPlannerIntentAppendixRef,
  planIdeaCardsQuota,
  refreshPlanItineraryFromServer,
}: PlanningTabProps) {
  logger.debug('[PlanningTab] Component rendering', { tripId: trip?.id, destination: trip?.destination });
  const theme = useTheme();
  const tripWithOwner = useMemo(() => {
    if (!trip) {
      return null as unknown as Trip;
    }
    const owner =
      trip.owner ??
      (trip as any)?.ownerId ??
      (trip as any)?.createdBy ??
      userId;
    if (owner && owner !== trip.owner) {
      return { ...trip, owner };
    }
    return trip;
  }, [trip, userId]);

  const safeTripStartDate = useMemo(
    () => (trip ? coerceDate(trip.startDate) : null),
    [trip?.startDate],
  );
  const safeTripEndDate = useMemo(
    () => (trip ? coerceDate(trip.endDate) ?? safeTripStartDate : null),
    [trip?.endDate, trip, safeTripStartDate],
  );
  const isStagingDebugLocal = useMemo(() => isInternalDebugBuild(), []);

  const invalidTrip = !trip?.id;
  const missingTripDates =
    !invalidTrip && (!safeTripStartDate || !safeTripEndDate);
  if (invalidTrip) {
    logger.error('[PlanningTab] Invalid trip object', undefined, { trip });
  } else if (missingTripDates) {
    logger.error('[PlanningTab] Trip missing required dates', undefined, { trip });
  }

  return (
    <>
      {invalidTrip ? (
        <View style={{ padding: theme.spacing(4), alignItems: 'center' }}>
          <ThemedText variant="bodyStrong" color={theme.colors.error}>
            Invalid trip data. Please try selecting a different trip.
          </ThemedText>
        </View>
      ) : missingTripDates ? (
        <View style={{ padding: theme.spacing(4), alignItems: 'center' }}>
          <ThemedText variant="bodyStrong" color={theme.colors.error}>
            Trip is missing required date information.
          </ThemedText>
        </View>
      ) : (
        <PlanningTabBody
          trip={trip}
          itineraryItems={itineraryItems}
          itineraryLoading={itineraryLoading}
          bookings={bookings}
          isArchived={isArchived}
          canEdit={canEdit}
          userId={userId}
          userPermission={userPermission}
          isOwner={isOwner}
          onAddItem={onAddItem}
          onEditItem={onEditItem}
          onDeleteItem={onDeleteItem}
          onViewDocument={onViewDocument}
          onOpenEmailProvider={onOpenEmailProvider}
          inboxSyncing={inboxSyncing}
          inboxConnected={inboxConnected}
          inboxProviderLabel={inboxProviderLabel}
          onLaunchRebooking={onLaunchRebooking}
          onLaunchDisruption={onLaunchDisruption}
          onLaunchBudget={onLaunchBudget}
          copilotLoading={copilotLoading}
          onOpenTravelCompanion={onOpenTravelCompanion}
          aiSuggestions={aiSuggestions}
          aiSuggestionsLoading={aiSuggestionsLoading}
          aiSuggestionsError={aiSuggestionsError}
          aiSuggestionsErrorMode={aiSuggestionsErrorMode}
          aiSuggestionsLoadingExtended={aiSuggestionsLoadingExtended}
          aiSuggestionsRequestMode={aiSuggestionsRequestMode}
          onGenerateSuggestions={onGenerateSuggestions}
          onCancelGenerateSuggestions={onCancelGenerateSuggestions}
          onAddRecommendationToItinerary={onAddRecommendationToItinerary}
          onApplyAllSuggestions={onApplyAllSuggestions}
          applyingSuggestions={applyingSuggestions}
          onAddSingleSuggestion={onAddSingleSuggestion}
          addingSuggestionIds={addingSuggestionIds}
          EnhancedSuggestionCard={EnhancedSuggestionCard}
          mapToEnhancedSuggestion={mapToEnhancedSuggestion}
          profile={profile}
          onSearchFlights={onSearchFlights}
          onSearchHotels={onSearchHotels}
          onSmartUpload={onSmartUpload}
          smartUploadLoading={smartUploadLoading}
          onOpenOfflineDownload={onOpenOfflineDownload}
          readinessAlerts={readinessAlerts}
          onOpenAlerts={onOpenAlerts}
          onAlertAction={onAlertAction}
          readinessCollapsed={readinessCollapsed}
          onToggleReadiness={onToggleReadiness}
          showTripOverviewCard={showTripOverviewCard}
          openCollaboratorSuggestToken={openCollaboratorSuggestToken}
          openCommentsToken={openCommentsToken}
          onScheduleMaybeItem={onScheduleMaybeItem}
          onIdeasModalVisibilityChange={onIdeasModalVisibilityChange}
          ideasModalReopenToken={ideasModalReopenToken}
          onTripOfflineCacheChanged={onTripOfflineCacheChanged}
          aiPlannerIntentAppendixRef={aiPlannerIntentAppendixRef}
          planIdeaCardsQuota={planIdeaCardsQuota}
          refreshPlanItineraryFromServer={refreshPlanItineraryFromServer}
          safeTripStartDate={safeTripStartDate as Date}
          safeTripEndDate={safeTripEndDate as Date}
          isStagingDebug={isStagingDebugLocal}
          tripWithOwner={tripWithOwner}
        />
      )}
    </>
  );
}

type CopilotQuickActionsProps = {
  onLaunchRebooking?: () => void;
  onLaunchDisruption?: () => void;
  onLaunchBudget?: () => void;
  loading?: boolean;
};

function CopilotQuickActions({
  onLaunchRebooking,
  onLaunchDisruption,
  onLaunchBudget,
  loading = false,
}: CopilotQuickActionsProps) {
  const theme = useTheme();
  const buttons = [
    {
      label: 'Rebooking help',
      icon: 'airplane',
      action: onLaunchRebooking,
    },
    {
      label: 'Disruption guard',
      icon: 'alert-circle',
      action: onLaunchDisruption,
    },
    {
      label: 'Budget coach',
      icon: 'wallet',
      action: onLaunchBudget,
    },
  ].filter((btn) => Boolean(btn.action));

  if (!buttons.length) return null;

  return (
    <Card
      variant="outlined"
      style={{
        marginTop: theme.spacing(1),
        padding: theme.spacing(1.5),
        gap: theme.spacing(1),
      }}
    >
      <ThemedText variant="h3" style={{ fontSize: 16 }}>
        AI Copilot
      </ThemedText>
      <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
        {buttons.map((button) => (
          <Pressable
            key={button.label}
            onPress={button.action}
            disabled={loading}
            style={({ pressed }) => ({
              flex: 1,
              paddingVertical: theme.spacing(1.25),
              borderRadius: theme.radii.md,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing(0.5),
              opacity: pressed || loading ? 0.6 : 1,
            })}
          >
            <Ionicons
              name={button.icon as any}
              size={18}
              color={theme.colors.primary}
            />
            <Caption
              color={theme.colors.text}
              style={{ fontSize: 12, fontWeight: '600', textAlign: 'center' }}
            >
              {loading ? 'Working...' : button.label}
            </Caption>
          </Pressable>
        ))}
      </View>
    </Card>
  );
}

function TripReadinessCard({
  cues,
  onOpenAlerts,
  onAlertAction,
  collapsed,
  onToggle,
}: {
  cues?: TripAlert[];
  onOpenAlerts?: () => void;
  onAlertAction?: (cue: TripAlert) => void;
  collapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
}) {
  const theme = useTheme();
  if (!cues || cues.length === 0) return null;

  const topCues = cues.slice(0, 2);
  const firstCue = topCues[0];

  return (
    <CollapsibleSection
      title="Trip readiness"
      icon="alert-circle-outline"
      expanded={!collapsed}
      onExpandedChange={(expanded) => onToggle?.(!expanded)}
      rightAction={
        onOpenAlerts ? (
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              onOpenAlerts();
            }}
            style={({ pressed }) => ({
              paddingHorizontal: theme.spacing(1),
              paddingVertical: theme.spacing(0.5),
              borderRadius: theme.radii.sm,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
              View all
            </Caption>
          </Pressable>
        ) : null
      }
    >
      <View style={{ gap: theme.spacing(1) }}>
        {topCues.map((cue) => (
          <View
            key={cue.id}
            style={{
              padding: theme.spacing(1),
              borderRadius: theme.radii.md,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              backgroundColor: theme.colors.surface,
              gap: theme.spacing(0.25),
            }}
          >
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
              {cue.priority === 'high'
                ? 'High priority'
                : cue.priority === 'medium'
                  ? 'Upcoming'
                  : 'Low priority'}
            </Caption>
            <ThemedText variant="bodyStrong">{cue.title}</ThemedText>
            <Body color={theme.colors.textSecondary}>{cue.message}</Body>
          </View>
        ))}
      </View>

      {firstCue && onAlertAction && (
        <Button
          label={firstCue.actionLabel || 'Fix now'}
          onPress={() => onAlertAction(firstCue)}
          variant="primary"
        />
      )}
    </CollapsibleSection>
  );
}

// Consolidated Trip Status Banner - dismissible alerts in a single compact strip
function TripStatusBanner({
  trip,
  userId,
  visaCheck,
  onVisaLinkPress,
  citizenshipLabel,
  itineraryItems,
  bookings,
  readinessAlerts,
  onOpenAlerts,
  onOpenOfflineDownload,
  onToggleReadiness,
  isDismissed,
  onDismiss,
}: {
  trip: Trip;
  /** When set, collaborator “add people” cues match generateTripAlerts / Trip Alerts modal. */
  userId?: string;
  visaCheck: VisaCheckResult;
  onVisaLinkPress: () => void;
  citizenshipLabel?: string;
  itineraryItems: ItineraryItem[];
  bookings: Booking[];
  readinessAlerts?: TripAlert[];
  onOpenAlerts?: () => void;
  onOpenOfflineDownload?: () => void;
  onToggleReadiness?: (collapsed: boolean) => void;
  isDismissed?: boolean;
  onDismiss?: () => void;
}) {
  const theme = useTheme();
  const isPremium = useUserStore(selectHasPremiumAccess);
  const [localDismissed, setLocalDismissed] = useState(isDismissed ?? false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [clearedAlertIds, setClearedAlertIds] = useState<Set<string>>(new Set());
  const [visaReadFromDisk, setVisaReadFromDisk] = useState<number | null>(null);

  const visaAlertId = `visa-${trip.id}`;
  const collaboratorsAlertId = `collaborators-${trip.id}`;
  const selectVisaReadAt = useCallback(
    (s: ReturnType<typeof useNotificationStore.getState>) => s.items[visaAlertId]?.readAt ?? null,
    [visaAlertId],
  );
  const storeVisaReadAt = useNotificationStore(selectVisaReadAt);

  useEffect(() => {
    let cancelled = false;
    getTripAlertReadTimestamps()
      .then((map) => {
        if (cancelled) return;
        const t = map[visaAlertId];
        setVisaReadFromDisk(typeof t === 'number' && Number.isFinite(t) ? t : null);
      })
      .catch(() => {
        if (!cancelled) setVisaReadFromDisk(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visaAlertId]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const cached = await getCachedTripData(trip.id);
        setIsDownloaded(!!cached);
      } catch (error) {
        logger.error('[TripStatusBanner] Error checking offline status', error instanceof Error ? error : new Error(String(error)));
      }
    };
    checkStatus();
  }, [trip.id]);

  // Respect legacy "cleared alerts" state so items a user cleared previously
  // do not re-appear as "needs attention" when new banner flows are introduced.
  useEffect(() => {
    let cancelled = false;
    getClearedAlerts()
      .then((ids) => {
        if (!cancelled) setClearedAlertIds(ids);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, [trip.id]);

  // Count actionable items
  const alertCount = readinessAlerts?.length || 0;
  const offlineAttentionId = `offline-${trip.id}`;
  const visaChecklistMarkedDone =
    (storeVisaReadAt != null && storeVisaReadAt > 0) ||
    (visaReadFromDisk != null && visaReadFromDisk > 0);
  const needsVisa =
    Boolean(visaCheck && (visaCheck.status === 'required' || visaCheck.status === 'check_required')) &&
    !clearedAlertIds.has(visaAlertId) &&
    !visaChecklistMarkedDone;
  const needsOffline = isPremium && !isDownloaded && !clearedAlertIds.has(offlineAttentionId);

  const isTripOwner = Boolean(userId && trip.owner === userId);
  const needsCollaborators =
    isTripOwner &&
    !hasCollaborators(trip) &&
    !clearedAlertIds.has(collaboratorsAlertId);

  const totalItems =
    alertCount + (needsVisa ? 1 : 0) + (needsOffline ? 1 : 0) + (needsCollaborators ? 1 : 0);

  // If nothing to show or dismissed, return null
  if (totalItems === 0 || localDismissed) {
    return null;
  }

  // Build summary text
  const items: string[] = [];
  if (alertCount > 0) items.push(`${alertCount} readiness item${alertCount > 1 ? 's' : ''}`);
  if (needsVisa) items.push('Visa check');
  if (needsOffline) items.push('Offline download');
  if (needsCollaborators) items.push('Add collaborators');

  const handleDismiss = () => {
    setLocalDismissed(true);
    onDismiss?.();
  };

  const handleReviewPress = () => {
    // If there are trip-readiness alerts, open the alerts UI.
    if (alertCount > 0 || needsCollaborators) {
      onOpenAlerts?.();
      return;
    }
    // If multiple non-readiness actions are pending, let the user choose.
    if (needsVisa && needsOffline) {
      Alert.alert('Review items', 'Choose what to review:', [
        { text: 'Visa check', onPress: onVisaLinkPress },
        { text: 'Offline download', onPress: () => onOpenOfflineDownload?.() },
        { text: 'Cancel', style: 'cancel' },
      ]);
      return;
    }
    // Otherwise, route to the most relevant remaining action so "Review" is never empty.
    if (needsVisa) {
      onVisaLinkPress();
      return;
    }
    if (needsOffline) {
      onOpenOfflineDownload?.();
    }
  };

  return (
    <Pressable
      onPress={handleReviewPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing(1.5),
        paddingVertical: theme.spacing(1),
        borderRadius: theme.radii.md,
        backgroundColor: theme.colors.warning + '15',
        borderWidth: 1,
        borderColor: theme.colors.warning + '40',
        marginBottom: theme.spacing(1.5),
        opacity: pressed ? 0.85 : 1,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
        <View style={{
          width: 24,
          height: 24,
          borderRadius: 12,
          backgroundColor: theme.colors.warning + '25',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Ionicons name="alert-circle" size={14} color={theme.colors.warning} />
        </View>
        <View style={{ flex: 1 }}>
          <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>
            {totalItems === 1 ? '1 item needs attention' : `${totalItems} items need attention`}
          </Caption>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }} numberOfLines={1}>
            {items.join(' • ')}
          </Caption>
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            handleReviewPress();
          }}
          style={({ pressed }) => ({
            paddingHorizontal: theme.spacing(1),
            paddingVertical: theme.spacing(0.5),
            borderRadius: theme.radii.sm,
            backgroundColor: pressed ? theme.colors.warning + '30' : 'transparent',
          })}
        >
          <Caption style={{ fontSize: 11, fontWeight: '700', color: theme.colors.primary }}>
            Review
          </Caption>
        </Pressable>
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            handleDismiss();
          }}
          style={({ pressed }) => ({
            padding: theme.spacing(0.5),
            borderRadius: theme.radii.sm,
            opacity: pressed ? 0.6 : 1,
          })}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="close" size={16} color={theme.colors.textSecondary} />
        </Pressable>
      </View>
    </Pressable>
  );
}

type TripOverviewCardProps = {
  trip: Trip;
  userId?: string;
  visaCheck: VisaCheckResult;
  onVisaLinkPress: () => void;
  citizenshipLabel?: string;
  onOpenFullMap?: () => void;
  itineraryItems: ItineraryItem[];
  bookings: Booking[];
  readinessAlerts?: TripAlert[];
  onOpenAlerts?: () => void;
  onAlertAction?: (alert: TripAlert) => void;
  readinessCollapsed?: boolean;
  onToggleReadiness?: (collapsed: boolean) => void;
  onOpenOfflineDownload?: () => void;
};

// Keep the old TripOverviewCard for backwards compatibility but simplified
export function TripOverviewCard({
  trip,
  userId,
  visaCheck,
  onVisaLinkPress,
  citizenshipLabel,
  onOpenFullMap,
  itineraryItems,
  bookings,
  readinessAlerts,
  onOpenAlerts,
  onAlertAction,
  readinessCollapsed,
  onToggleReadiness,
  onOpenOfflineDownload,
}: TripOverviewCardProps) {
  return (
    <TripStatusBanner
      trip={trip}
      userId={userId}
      visaCheck={visaCheck}
      onVisaLinkPress={onVisaLinkPress}
      citizenshipLabel={citizenshipLabel}
      itineraryItems={itineraryItems}
      bookings={bookings}
      readinessAlerts={readinessAlerts}
      onOpenAlerts={onOpenAlerts}
      onOpenOfflineDownload={onOpenOfflineDownload}
      onToggleReadiness={onToggleReadiness}
    />
  );
}

function OverviewStat({ label, value }: { label: string; value?: string | number }) {
  const theme = useTheme();

  if (!value) {
    return null;
  }

  return (
    <View style={{ minWidth: 90 }}>
      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
        {label}
      </Caption>
      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
        {value}
      </ThemedText>
    </View>
  );
}

function VisaInlineNotice({
  visaCheck,
  onPress,
  destination,
  citizenship,
}: {
  visaCheck: VisaCheckResult;
  onPress: () => void;
  destination?: string;
  citizenship?: string;
}) {
  const theme = useTheme();
  if (!visaCheck) {
    return null;
  }

  const statusColor =
    visaCheck.status === 'not_required'
      ? theme.colors.success
      : visaCheck.status === 'required'
        ? theme.colors.error
        : theme.colors.warning;

  return (
    <View
      style={{
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
        borderRadius: theme.radii.md,
        padding: theme.spacing(1.5),
        backgroundColor: theme.colors.surface,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
        <Ionicons name="shield-checkmark-outline" size={18} color={statusColor} />
        <ThemedText variant="bodyStrong">Visa information</ThemedText>
      </View>
      <Body color={theme.colors.text} style={{ marginTop: theme.spacing(0.5) }}>
        {visaCheck.details ||
          getVisaStatusMessageLegacy(visaCheck.status, destination || '', citizenship || '')}
      </Body>
      <Pressable
        onPress={onPress}
        style={({ pressed }) => ({
          marginTop: theme.spacing(0.5),
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.25),
          opacity: pressed ? 0.7 : 1,
        })}
      >
        <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
          Check requirements
        </Caption>
        <Ionicons name="open-outline" size={14} color={theme.colors.primary} />
      </Pressable>
    </View>
  );
}

function PlanningIdeasPreview({
  aiSuggestions,
  aiSuggestionsLoading,
  localPicksRecommendations,
  localPicksLoading,
  forYouCount = 0,
  onGenerateSuggestions,
  onOpenIdeas,
}: {
  aiSuggestions?: ChatPlannerResult | null;
  aiSuggestionsLoading?: boolean;
  localPicksRecommendations: LocalPicksRecommendationItem[];
  localPicksLoading?: boolean;
  forYouCount?: number;
  onGenerateSuggestions?: (mode?: 'itinerary' | 'packages') => void;
  onOpenIdeas: (preferredTab?: LegacyIdeasTab) => void;
}) {
  const theme = useTheme();
  const aiCount = aiSuggestions?.suggestions?.length || 0;
  const localCount = localPicksRecommendations.length;
  const totalIdeas = aiCount + localCount + (forYouCount || 0);

  const handleGenerate = useCallback(() => {
    if (localCount > 0) {
      onOpenIdeas('localPicks');
      return;
    }
    onGenerateSuggestions?.('itinerary');
  }, [localCount, onGenerateSuggestions, onOpenIdeas]);

  return (
    <Card variant="outlined" style={{ borderRadius: theme.radii.lg, padding: theme.spacing(2) }}>
      <View style={{ gap: theme.spacing(1.5) }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <View>
            <ThemedText variant="h3" style={{ fontSize: 16, fontWeight: '700' }}>
              Inspiration & ideas
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>
              {totalIdeas ? `${totalIdeas} options ready to review` : 'Kickstart with AI in a tap'}
            </Caption>
          </View>
          {totalIdeas > 0 ? (
            <Button
              label={`Review ideas (${totalIdeas})`}
              size="small"
              onPress={() => onOpenIdeas()}
              style={{ flexShrink: 0, paddingHorizontal: theme.spacing(2) }}
            />
          ) : (
            <Button
              label={aiSuggestionsLoading ? 'Generating...' : 'Generate plan'}
              size="small"
              onPress={handleGenerate}
              disabled={aiSuggestionsLoading}
              style={{ flexShrink: 0, paddingHorizontal: theme.spacing(2) }}
            />
          )}
        </View>

        <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
          <IdeasChip
            icon="sparkles"
            label="AI plan"
            value={
              aiSuggestionsLoading
                ? 'Running...'
                : aiCount
                  ? `${aiCount} suggestion${aiCount === 1 ? '' : 's'}`
                  : 'Ready to run'
            }
            onPress={() => (aiCount ? onOpenIdeas('ai') : handleGenerate())}
          />
          <IdeasChip
            icon="map-outline"
            label="Local picks"
            value={
              localPicksLoading
                ? 'Refreshing...'
                : localCount
                  ? `${localCount} available`
                  : 'Searching nearby'
            }
            onPress={() => onOpenIdeas('localPicks')}
          />
          <IdeasChip
            icon="heart-outline"
            label="For you"
            value={forYouCount ? `${forYouCount} pick${forYouCount === 1 ? '' : 's'}` : 'Personalized'}
            onPress={() => onOpenIdeas('for_you')}
          />
        </View>
      </View>
    </Card>
  );
}

function IdeasChip({
  icon,
  label,
  value,
  onPress,
}: {
  icon: string;
  label: string;
  value: string;
  onPress?: () => void;
}) {
  const theme = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        padding: theme.spacing(1.25),
        borderRadius: theme.radii.md,
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
        backgroundColor: theme.colors.surface,
        opacity: pressed ? 0.75 : 1,
      })}
    >
      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
        {label}
      </Caption>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
        <Ionicons name={icon as any} size={14} color={theme.colors.primary} />
        <Body style={{ fontSize: 13, fontWeight: '600' }}>{value}</Body>
      </View>
    </Pressable>
  );
}

function ToolsRow({
  icon,
  title,
  subtitle,
  badgeCount,
  expanded,
  onPress,
  themeColor,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  subtitle?: string;
  badgeCount?: number;
  expanded?: boolean;
  onPress: () => void;
  themeColor: string;
}) {
  const theme = useTheme();
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: theme.spacing(1),
        paddingHorizontal: theme.spacing(0.25),
        borderRadius: theme.radii.md,
        backgroundColor: pressed ? theme.colors.borderMuted + '30' : 'transparent',
      })}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: themeColor + '15',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ionicons name={icon} size={18} color={themeColor} />
        </View>
        <View style={{ flex: 1 }}>
          <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
            {title}
          </ThemedText>
          {subtitle ? (
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }} numberOfLines={1}>
              {subtitle}
            </Caption>
          ) : null}
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
        {(badgeCount || 0) > 0 && (
          <View
            style={{
              backgroundColor: themeColor + '15',
              paddingHorizontal: theme.spacing(1),
              paddingVertical: theme.spacing(0.35),
              borderRadius: theme.radii.pill,
            }}
          >
            <Caption style={{ fontSize: 11, fontWeight: '800', color: themeColor }}>
              {badgeCount}
            </Caption>
          </View>
        )}
        <Ionicons
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={theme.colors.textSecondary}
        />
      </View>
    </Pressable>
  );
}

// Compact Ideas Row for Trip tools section
function CompactIdeasRow({
  totalIdeas,
  onOpenIdeas,
  aiSuggestionsLoading,
}: {
  totalIdeas: number;
  onOpenIdeas: (preferredTab?: LegacyIdeasTab) => void;
  aiSuggestionsLoading?: boolean;
}) {
  const theme = useTheme();
  return (
    <Pressable
      onPress={() => onOpenIdeas()}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: theme.spacing(1),
        paddingHorizontal: theme.spacing(0.25),
        borderRadius: theme.radii.md,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            backgroundColor: theme.colors.primary + '15',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
        </View>
        <View>
          <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
            Inspiration & ideas
          </ThemedText>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
            {totalIdeas > 0 ? `${totalIdeas} options ready` : aiSuggestionsLoading ? 'Generating...' : 'AI plans, local picks'}
          </Caption>
        </View>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
        {totalIdeas > 0 && (
          <View
            style={{
              backgroundColor: theme.colors.primary + '15',
              paddingHorizontal: theme.spacing(1),
              paddingVertical: theme.spacing(0.35),
              borderRadius: theme.radii.pill,
            }}
          >
            <Caption style={{ fontSize: 11, fontWeight: '800', color: theme.colors.primary }}>
              {totalIdeas}
            </Caption>
          </View>
        )}
        <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
      </View>
    </Pressable>
  );
}

const AI_PLANNER_FOCUS_OPTIONS: Array<{ id: string; label: string; appendix: string | null }> = [
  { id: 'balanced', label: 'Balanced', appendix: null },
  {
    id: 'food',
    label: 'Food',
    appendix:
      'Prioritize exceptional dining, food halls, markets, and local culinary experiences.',
  },
  {
    id: 'culture',
    label: 'Culture',
    appendix:
      'Prioritize museums, history, architecture, galleries, and performing arts.',
  },
  {
    id: 'nature',
    label: 'Nature',
    appendix:
      'Prioritize parks, gardens, viewpoints, easy outdoor activities, and scenic spots.',
  },
  {
    id: 'nightlife',
    label: 'Nightlife',
    appendix:
      'Prioritize evening entertainment, live music, rooftops, and safe nightlife districts.',
  },
];

type PlanningIdeasModalProps = {
  visible: boolean;
  onClose: () => void;
  activeTab: IdeasTab;
  onTabChange: (tab: IdeasTab) => void;
  currentDay?: number;
  currentLocation?: LocationCoordinates;
  aiSuggestions?: ChatPlannerResult | null;
  aiSuggestionsLoading?: boolean;
  aiSuggestionsError?: string | null;
  aiSuggestionsErrorMode?: 'itinerary' | 'packages' | null;
  aiSuggestionsLoadingExtended?: boolean;
  aiSuggestionsRequestMode?: 'itinerary' | 'packages' | null;
  onGenerateSuggestions?: (mode?: 'itinerary' | 'packages') => void;
  onCancelGenerateSuggestions?: () => void;
  /** Collapse full-screen Ideas while AI keeps running (Plan screen shows a reopen chip). */
  onMinimizeIdeas?: () => void;
  onApplyAllSuggestions?: () => void;
  applyingSuggestions?: boolean;
  onAddSingleSuggestion?: (
    suggestion: any,
    picked?: { date: Date; startTime?: string }
  ) => void;
  addingSuggestionIds: Set<string>;
  EnhancedSuggestionCard?: React.ComponentType<any>;
  mapToEnhancedSuggestion?: (suggestion: any, destination?: string) => any;
  trip: Trip;
  itineraryItems: ItineraryItem[];
  localPicksRecommendations: LocalPicksRecommendationItem[];
  localPicksLoading: boolean;
  localPicksError?: string | null;
  onRefreshLocalPicks: () => void;
  onAddRecommendationToItinerary?: (item: {
    title: string;
    description?: string;
    category: ItineraryItemCategory;
    startDate: Date;
    startTime?: string; // Time string (HH:mm)
    endTime?: string; // Time string (HH:mm)
    address?: string;
    location?: string;
  }) => Promise<void>;
  onAfterApplyAll?: () => void;
  onMaybeAdded?: () => void;
  userId?: string;
  aiPlannerIntentAppendixRef?: MutableRefObject<string | null>;
  /** Omit when premium. `null` while the usage snapshot is loading. */
  planIdeaCardsQuota?: AiPlanIdeaCardsQuotaView | null;
};

function PlanningIdeasModal({
  visible,
  onClose,
  activeTab,
  onTabChange,
  currentDay,
  currentLocation,
  aiSuggestions,
  aiSuggestionsLoading,
  aiSuggestionsError,
  aiSuggestionsErrorMode,
  aiSuggestionsLoadingExtended,
  aiSuggestionsRequestMode,
  onGenerateSuggestions,
  onCancelGenerateSuggestions,
  onMinimizeIdeas,
  onApplyAllSuggestions,
  applyingSuggestions,
  onAddSingleSuggestion,
  addingSuggestionIds,
  EnhancedSuggestionCard,
  mapToEnhancedSuggestion,
  trip,
  itineraryItems,
  localPicksRecommendations,
  localPicksLoading,
  localPicksError,
  onRefreshLocalPicks,
  onAddRecommendationToItinerary,
  onAfterApplyAll,
  onMaybeAdded,
  userId,
  aiPlannerIntentAppendixRef,
  planIdeaCardsQuota,
}: PlanningIdeasModalProps) {
  const { showError, showToast } = useToast();
  const theme = useTheme();
  const [selectedAiFocusId, setSelectedAiFocusId] = useState('balanced');
  const safeItineraryItems = Array.isArray(itineraryItems) ? itineraryItems : [];
  const safeLocalPicksRecommendations = Array.isArray(localPicksRecommendations) ? localPicksRecommendations : [];

  useEffect(() => {
    const opt = AI_PLANNER_FOCUS_OPTIONS.find((o) => o.id === selectedAiFocusId);
    if (aiPlannerIntentAppendixRef) {
      aiPlannerIntentAppendixRef.current = opt?.appendix ?? null;
    }
  }, [selectedAiFocusId, aiPlannerIntentAppendixRef]);

  const handleIdeasModalClose = useCallback(() => {
    if (aiSuggestionsLoading && onMinimizeIdeas) {
      onMinimizeIdeas();
      return;
    }
    onClose();
  }, [aiSuggestionsLoading, onMinimizeIdeas, onClose]);
  
  // Debug logging for handler props (helps diagnose "button does nothing" issues)
  useEffect(() => {
    if (visible && __DEV__) {
      console.log('[PlanningIdeasModal] Handler props status', {
        hasOnAddSingleSuggestion: !!onAddSingleSuggestion,
        hasOnAddRecommendationToItinerary: !!onAddRecommendationToItinerary,
        hasOnGenerateSuggestions: !!onGenerateSuggestions,
        tripId: trip?.id,
        userId,
      });
    }
  }, [visible, onAddSingleSuggestion, onAddRecommendationToItinerary, onGenerateSuggestions, trip?.id, userId]);
  const insets = useSafeAreaInsets();
  const { height: screenHeight } = Dimensions.get('window');
  const ideasSheetMaxHeight = useMemo(
    () => Math.max(Math.min(screenHeight - insets.top - 60, screenHeight * 0.95), screenHeight * 0.6),
    [insets.top, screenHeight],
  );
  const LOCAL_PICKS_INITIAL_LIMIT = 12;
  const LOCAL_PICKS_SKELETON_COUNT = 4;
  const [localPicksExpanded, setLocalPicksExpanded] = useState(false);
  const [pickDayVisible, setPickDayVisible] = useState(false);
  const [pickDayTitle, setPickDayTitle] = useState<string>('');
  const pickDayResolveRef = useRef<((date: Date | null) => void) | null>(null);
  const pickDayVisibleRef = useRef(false);
  const [pickDayDefault, setPickDayDefault] = useState<Date | null>(null);
  const [pickTimeVisible, setPickTimeVisible] = useState(false);
  const [pickTimeTitle, setPickTimeTitle] = useState<string>('');
  const [pickTimeDate, setPickTimeDate] = useState<Date | null>(null);
  const pickTimeResolveRef = useRef<
    ((picked: { startTime?: string; endTime?: string } | null) => void) | null
  >(null);
  const pickTimeVisibleRef = useRef(false);
  const [inlineToast, setInlineToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);
  const inlineToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [addedItineraryKeys, setAddedItineraryKeys] = useState<Set<string>>(new Set());
  const tripOwnerId = useMemo(() => (trip as any)?.owner || userId || '', [trip, userId]);
  const tripWithOwner = useMemo(() => (tripOwnerId && !(trip as any)?.owner ? { ...trip, owner: tripOwnerId } : trip), [trip, tripOwnerId]);

  const localPicksDestinationHint = useMemo(() => {
    const label = getTripDestinationLabel(trip)?.trim();
    if (label) return label;
    return (trip.destination || '').trim();
  }, [trip]);

  const tripDateRange = useMemo(
    () => deriveTripDateRange(trip, safeItineraryItems),
    [trip, safeItineraryItems],
  );
  const safeTripStartDate = useMemo(
    () => coerceDate(tripDateRange.start) ?? coerceDate(trip?.startDate),
    [tripDateRange.start, trip?.startDate],
  );
  const safeTripEndDate = useMemo(
    () => coerceDate(tripDateRange.end) ?? coerceDate(trip?.endDate) ?? safeTripStartDate,
    [tripDateRange.end, trip?.endDate, safeTripStartDate],
  );

  const tripDates = useMemo(() => {
    const derived = expandDateRangeToDays(tripDateRange.start, tripDateRange.end);
    const fallbackStart = coerceDate(trip?.startDate) ?? derived[0];
    if (!fallbackStart) return derived;
    // When trip is single-day or range is missing, always offer 14 days so user can pick any day
    if (derived.length > 1) return derived;
    const fallbackEnd = coerceDate(trip?.endDate);
    if (fallbackStart && fallbackEnd) {
      const expanded = expandDateRangeToDays(fallbackStart, fallbackEnd);
      if (expanded.length > 1) return expanded;
    }
    const tempEnd = new Date(fallbackStart);
    tempEnd.setDate(tempEnd.getDate() + 13);
    return expandDateRangeToDays(fallbackStart, tempEnd);
  }, [trip?.startDate, trip?.endDate, tripDateRange.end, tripDateRange.start]);

  const projectedPlanAiBatch = useMemo(() => {
    const a = safeTripStartDate?.getTime() ?? 0;
    const b = safeTripEndDate?.getTime() ?? a;
    const days = Math.ceil((b - a) / MS_PER_DAY);
    return Math.min(Math.max(PLAN_AI_IDEA_BATCH.min, days), PLAN_AI_IDEA_BATCH.max);
  }, [safeTripStartDate, safeTripEndDate]);

  const planIdeasQuotaBlocked =
    planIdeaCardsQuota !== undefined &&
    planIdeaCardsQuota !== null &&
    planIdeaCardsQuota.remaining < projectedPlanAiBatch;

  const planIdeaQuotaEl = useMemo(() => {
    if (planIdeaCardsQuota === undefined || planIdeaCardsQuota === null) return null;
    return (
      <Card
        variant="outlined"
        style={{
          padding: theme.spacing(1.25),
          marginBottom: theme.spacing(0.5),
          alignSelf: 'stretch',
          width: '100%',
          borderColor: planIdeasQuotaBlocked ? theme.colors.warning : theme.colors.borderMuted,
        }}
      >
        <Caption
          color={planIdeasQuotaBlocked ? theme.colors.warning : theme.colors.textSecondary}
          style={{ textAlign: 'center' }}
        >
          {planIdeasQuotaBlocked
            ? `${planIdeaCardsQuota.remaining} idea card(s) left — a full run uses up to ${projectedPlanAiBatch}. Upgrade for unlimited Plan AI.`
            : `${planIdeaCardsQuota.remaining} of ${planIdeaCardsQuota.limit} Plan AI idea cards left this month.`}
        </Caption>
      </Card>
    );
  }, [
    planIdeaCardsQuota,
    planIdeasQuotaBlocked,
    projectedPlanAiBatch,
    theme.colors.borderMuted,
    theme.colors.textSecondary,
    theme.colors.warning,
    theme.spacing,
  ]);

  const showInlineToast = useCallback(
    (message: string, type: 'success' | 'info' | 'error', duration = 3000) => {
      setInlineToast({ message, type });
      if (inlineToastTimerRef.current) {
        clearTimeout(inlineToastTimerRef.current);
      }
      inlineToastTimerRef.current = setTimeout(() => {
        setInlineToast(null);
        inlineToastTimerRef.current = null;
      }, duration);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (inlineToastTimerRef.current) {
        clearTimeout(inlineToastTimerRef.current);
        inlineToastTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    pickDayVisibleRef.current = pickDayVisible;
  }, [pickDayVisible]);

  useEffect(() => {
    pickTimeVisibleRef.current = pickTimeVisible;
  }, [pickTimeVisible]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (nextState) => {
      if (nextState !== 'active') return;
      if (pickDayResolveRef.current && !pickDayVisibleRef.current) {
        requestAnimationFrame(() => setPickDayVisible(true));
      }
      if (pickTimeResolveRef.current && !pickTimeVisibleRef.current) {
        requestAnimationFrame(() => setPickTimeVisible(true));
      }
    });
    return () => sub.remove();
  }, []);

  const formatDayLabel = (date: Date) => {
    if (!Number.isFinite(date.getTime())) {
      return 'Date TBD';
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const promptForTripDay = useCallback(
    (
      title: string,
      defaultDate?: Date | null,
      opts?: { dismissIdeas?: boolean },
    ): Promise<Date | null> => {
      if (isUiDebugLoggingEnabled()) {
        console.log('[Ideas] promptForTripDay', { title, dismissIdeas: opts?.dismissIdeas ?? true });
      }
      setPickDayTitle(title);
      setPickDayDefault(defaultDate ? new Date(defaultDate) : null);
      return new Promise<Date | null>((resolve) => {
        const forceShowDayPicker = () => {
          if (!pickDayResolveRef.current) return;
          setPickDayVisible(true);
          requestAnimationFrame(() => {
            if (pickDayResolveRef.current && !pickDayVisibleRef.current) {
              setPickDayVisible(true);
            }
          });
        };
        const watchdog = setTimeout(() => {
          if (pickDayResolveRef.current && !pickDayVisibleRef.current) {
            setPickDayVisible(true);
          }
        }, 900);
        const nudge = setTimeout(() => {
          forceShowDayPicker();
        }, 120);
        pickDayResolveRef.current = (date) => {
          clearTimeout(nudge);
          clearTimeout(watchdog);
          resolve(date);
        };
        forceShowDayPicker();
      });
    },
    [],
  );

  const promptForTripTime = useCallback(
    (
      title: string,
      date: Date,
      opts?: { dismissIdeas?: boolean },
    ): Promise<{ startTime?: string; endTime?: string } | null> => {
      if (isUiDebugLoggingEnabled()) {
        console.log('[Ideas] promptForTripTime', { title, dismissIdeas: opts?.dismissIdeas ?? true });
      }
      setPickTimeTitle(title);
      setPickTimeDate(new Date(date));
      return new Promise<{ startTime?: string; endTime?: string } | null>((resolve) => {
        const forceShowTimePicker = () => {
          if (!pickTimeResolveRef.current) return;
          setPickTimeVisible(true);
          requestAnimationFrame(() => {
            if (pickTimeResolveRef.current && !pickTimeVisibleRef.current) {
              setPickTimeVisible(true);
            }
          });
        };
        const watchdog = setTimeout(() => {
          if (pickTimeResolveRef.current && !pickTimeVisibleRef.current) {
            setPickTimeVisible(true);
          }
        }, 900);
        const nudge = setTimeout(() => {
          forceShowTimePicker();
        }, 120);
        pickTimeResolveRef.current = (picked) => {
          clearTimeout(nudge);
          clearTimeout(watchdog);
          resolve(picked);
        };
        forceShowTimePicker();
      });
    },
    [],
  );

  const promptForTripDateTime = useCallback(
    async (
      title: string,
      defaultDate?: Date | null,
      opts?: { dismissIdeas?: boolean },
    ): Promise<{ date: Date; startTime?: string; endTime?: string } | null> => {
      while (true) {
        const date = await promptForTripDay(title, defaultDate, opts);
        if (!date) return null;

        const picked = await promptForTripTime(title, date, opts);
        if (picked === null) return null; // cancelled
        // If time picker returns {} (e.g. "Pick another day" or no block chosen), treat as flexible time
        // so we don't re-prompt for day and avoid the date selector appearing again after add.
        if (!picked.startTime && !picked.endTime) {
          return { date, startTime: undefined, endTime: undefined };
        }
        return { date, startTime: picked.startTime, endTime: picked.endTime };
      }
    },
    [promptForTripDay, promptForTripTime],
  );

  // Reset expansion when switching tabs / reopening modal / refreshing results
  useEffect(() => {
    if (!visible) return;
    setLocalPicksExpanded(false);
  }, [visible, activeTab, safeLocalPicksRecommendations.length]);

  const rankLocalPicks = useCallback((items: LocalPicksRecommendationItem[]) => {
    if (!items?.length) return [];

    const categoryOrder = ['landmarks', 'dining', 'trendy'] as const;
    type CategoryKey = (typeof categoryOrder)[number] | 'other';

    // Preserve the incoming city order (insertion order)
    const byCity = new Map<string, LocalPicksRecommendationItem[]>();
    for (const it of items) {
      const city = String((it as any)?.metadata?.city || 'Local picks');
      if (!byCity.has(city)) byCity.set(city, []);
      byCity.get(city)!.push(it);
    }
    const cities = Array.from(byCity.keys());

    // Split each city into category queues and sort within each queue by "best first"
    const cityQueues = new Map<string, Record<CategoryKey, LocalPicksRecommendationItem[]>>();
    for (const city of cities) {
      const grouped: Record<CategoryKey, LocalPicksRecommendationItem[]> = {
        landmarks: [],
        dining: [],
        trendy: [],
        other: [],
      };

      for (const it of byCity.get(city) || []) {
        const raw = (it as any)?.metadata?.category;
        const cat: CategoryKey =
          raw === 'landmarks' || raw === 'dining' || raw === 'trendy' ? raw : 'other';
        grouped[cat].push(it);
      }

      const scoreSort = (a: LocalPicksRecommendationItem, b: LocalPicksRecommendationItem) => {
        const ar = typeof a.rating === 'number' ? a.rating : -1;
        const br = typeof b.rating === 'number' ? b.rating : -1;
        if (br !== ar) return br - ar;
        const au = Number((a as any)?.metadata?.userRatingsTotal || 0);
        const bu = Number((b as any)?.metadata?.userRatingsTotal || 0);
        if (bu !== au) return bu - au;
        return String(a.name).localeCompare(String(b.name));
      };

      grouped.landmarks.sort(scoreSort);
      grouped.dining.sort(scoreSort);
      grouped.trendy.sort(scoreSort);
      grouped.other.sort(scoreSort);

      cityQueues.set(city, grouped);
    }

    // Round-robin across cities and categories for a diverse first page.
    const categoryPointers = new Map<string, number>();
    cities.forEach((c) => categoryPointers.set(c, 0));

    const hasRemaining = () => {
      for (const city of cities) {
        const q = cityQueues.get(city)!;
        if (q.landmarks.length || q.dining.length || q.trendy.length || q.other.length) return true;
      }
      return false;
    };

    const pickNextForCity = (city: string) => {
      const q = cityQueues.get(city)!;
      const startIdx = categoryPointers.get(city) || 0;
      const cycle: CategoryKey[] = [...categoryOrder, 'other'];

      for (let i = 0; i < cycle.length; i++) {
        const idx = (startIdx + i) % cycle.length;
        const key = cycle[idx];
        if (q[key].length) {
          categoryPointers.set(city, (idx + 1) % cycle.length);
          return q[key].shift()!;
        }
      }
      return null;
    };

    const out: LocalPicksRecommendationItem[] = [];
    while (hasRemaining()) {
      for (const city of cities) {
        const next = pickNextForCity(city);
        if (next) out.push(next);
      }
    }
    return out;
  }, []);

  const orderedLocalPicks = useMemo(
    () => rankLocalPicks(safeLocalPicksRecommendations),
    [safeLocalPicksRecommendations, rankLocalPicks],
  );
  const visibleLocalPicks = useMemo(
    () => (localPicksExpanded ? orderedLocalPicks : orderedLocalPicks.slice(0, LOCAL_PICKS_INITIAL_LIMIT)),
    [localPicksExpanded, orderedLocalPicks],
  );
  const [addingLocalPicksId, setAddingLocalPicksId] = useState<string | null>(null);
  const [savingMaybeIds, setSavingMaybeIds] = useState<Set<string>>(new Set());
  const [forYouLoading, setForYouLoading] = useState(false);
  const [forYouError, setForYouError] = useState<string | null>(null);
  const [forYouRecommendations, setForYouRecommendations] = useState<PlaceRecommendation[]>([]);
  const [forYouRefreshToken, setForYouRefreshToken] = useState(0);
  // AI output is shared across "AI plan" + "Packages" tabs.
  // We track the last requested mode so the UI can render the right empty-state when users switch tabs.
  const [aiRequestMode, setAiRequestMode] = useState<'itinerary' | 'packages'>('itinerary');
  const pendingAiModeRef = useRef<'itinerary' | 'packages'>('itinerary');
  const [aiSuggestionsByMode, setAiSuggestionsByMode] = useState<{
    itinerary: ChatPlannerResult | null;
    packages: ChatPlannerResult | null;
  }>({ itinerary: null, packages: null });

  // Load cached AI output for instant display when opening the Ideas modal (itinerary cache is Plan-focus-specific).
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    const loadCached = async () => {
      const fk = buildPlannerFocusCacheKey(aiPlannerIntentAppendixRef?.current ?? null);
      const [it, pk] = await Promise.all([
        getCachedChatPlannerResult(trip.id, 'itinerary', fk),
        getCachedChatPlannerResult(trip.id, 'packages'),
      ]);
      if (cancelled) return;
      setAiSuggestionsByMode((prev) => ({
        // Prefer cache for this focus; if none, keep what we have (parent or other focus) + stale banner.
        itinerary: it?.result ?? prev.itinerary,
        packages: prev.packages || pk?.result || null,
      }));
    };
    void loadCached().catch(() => {
      // best-effort
    });
    return () => {
      cancelled = true;
    };
  }, [visible, trip.id, selectedAiFocusId, aiPlannerIntentAppendixRef]);

  useEffect(() => {
    if (!visible) {
      setAddingLocalPicksId(null);
    }
  }, [visible]);

  // When a new AI result arrives, assign it to the mode we last requested.
  useEffect(() => {
    if (!visible) return;
    if (aiSuggestionsLoading) return;
    if (!aiSuggestions) return;
    const mode = pendingAiModeRef.current;
    setAiSuggestionsByMode((prev) => ({ ...prev, [mode]: aiSuggestions }));
  }, [visible, aiSuggestions, aiSuggestionsLoading]);

  const itineraryAiCount = Array.isArray(aiSuggestionsByMode.itinerary?.suggestions)
    ? aiSuggestionsByMode.itinerary!.suggestions.length
    : 0;
  const packagesAiCount = Array.isArray(aiSuggestionsByMode.packages?.suggestions)
    ? aiSuggestionsByMode.packages!.suggestions.filter((s) => s && s.isPackage === true).length
    : 0;

  const currentPlannerFocusCacheKey = useMemo(
    () => buildPlannerFocusCacheKey(aiPlannerIntentAppendixRef?.current ?? null),
    [selectedAiFocusId],
  );

  const itinerarySuggestionsStale =
    Array.isArray(aiSuggestionsByMode.itinerary?.suggestions) &&
    aiSuggestionsByMode.itinerary!.suggestions.length > 0 &&
    ((aiSuggestionsByMode.itinerary?.debug as { plannerFocusCacheKey?: string } | undefined)
      ?.plannerFocusCacheKey ?? 'default') !== currentPlannerFocusCacheKey;

  const itineraryFocusStaleBanner = useMemo(() => {
    if (!itinerarySuggestionsStale || aiRequestMode !== 'itinerary') return null;
    const label =
      AI_PLANNER_FOCUS_OPTIONS.find((o) => o.id === selectedAiFocusId)?.label ?? 'this focus';
    return (
      <Card
        variant="outlined"
        style={{
          padding: theme.spacing(1.25),
          marginBottom: theme.spacing(1),
          borderColor: `${theme.colors.primary}66`,
          backgroundColor: `${theme.colors.primary}16`,
        }}
      >
        <View style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'flex-start' }}>
          <Ionicons name="information-circle" size={22} color={theme.colors.primary} style={{ marginTop: 2 }} />
          <View style={{ flex: 1, gap: theme.spacing(0.35) }}>
            <Body style={{ color: theme.colors.text, fontWeight: '600' }}>Plan focus updated</Body>
            <Caption color={theme.colors.textSecondary}>
              These ideas use an older focus. Generate again for{' '}
              <Caption style={{ fontWeight: '800', color: theme.colors.text }}>{label}</Caption>.
            </Caption>
            {onGenerateSuggestions ? (
              <Button
                label={`Generate for ${label}`}
                size="small"
                onPress={() => {
                  if (planIdeasQuotaBlocked) {
                    usePremiumGateStore.getState().open({
                      feature: 'ai_plan_ideas',
                      title: 'Not enough Plan AI cards',
                      message: `This run uses up to ${projectedPlanAiBatch} idea cards. You have ${planIdeaCardsQuota?.remaining ?? 0} left this month (${FREE_PLAN_LIMITS.aiPlanIdeaCardsPerMonth} included on the free plan). Upgrade for unlimited Plan AI ideas.`,
                      source: 'feature_gate',
                    });
                    return;
                  }
                  pendingAiModeRef.current = 'itinerary';
                  setAiRequestMode('itinerary');
                  onGenerateSuggestions('itinerary');
                }}
                disabled={aiSuggestionsLoading || planIdeasQuotaBlocked}
                style={{ marginTop: theme.spacing(0.75), alignSelf: 'flex-start' }}
              />
            ) : null}
          </View>
        </View>
      </Card>
    );
  }, [
    aiSuggestionsLoading,
    itinerarySuggestionsStale,
    aiRequestMode,
    onGenerateSuggestions,
    planIdeasQuotaBlocked,
    projectedPlanAiBatch,
    planIdeaCardsQuota?.remaining,
    selectedAiFocusId,
    theme.colors.primary,
    theme.colors.text,
    theme.colors.textSecondary,
    theme.spacing,
  ]);

  const ideaCount =
    itineraryAiCount +
    packagesAiCount +
    safeLocalPicksRecommendations.length +
    (forYouRecommendations.length || 0);

  const refreshForYou = useCallback(() => setForYouRefreshToken((t) => t + 1), []);

  const triggerAi = useCallback(
    (mode: 'itinerary' | 'packages') => {
      if (!onGenerateSuggestions) return;
      if (planIdeasQuotaBlocked) {
        usePremiumGateStore.getState().open({
          feature: 'ai_plan_ideas',
          title: 'Not enough Plan AI cards',
          message: `This run uses up to ${projectedPlanAiBatch} idea cards. You have ${planIdeaCardsQuota?.remaining ?? 0} left this month (${FREE_PLAN_LIMITS.aiPlanIdeaCardsPerMonth} included on the free plan). Upgrade for unlimited Plan AI ideas.`,
          source: 'feature_gate',
        });
        return;
      }
      pendingAiModeRef.current = mode;
      setAiRequestMode(mode);
      onGenerateSuggestions(mode);
    },
    [
      onGenerateSuggestions,
      planIdeasQuotaBlocked,
      projectedPlanAiBatch,
      planIdeaCardsQuota?.remaining,
    ],
  );

  const renderAiFocusChips = useCallback(() => {
    if (!onGenerateSuggestions || !aiPlannerIntentAppendixRef) return null;
    return (
      <View style={{ marginBottom: theme.spacing(1), gap: theme.spacing(0.5), width: '100%' }}>
        <Caption color={theme.colors.textSecondary}>Plan focus</Caption>
        <Caption color={theme.colors.textSecondary} style={{ opacity: 0.92, marginTop: -theme.spacing(0.25) }}>
          Applies the next time you generate (not retroactive).
        </Caption>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
          {AI_PLANNER_FOCUS_OPTIONS.map((opt) => {
            const selected = selectedAiFocusId === opt.id;
            return (
              <Pressable
                key={opt.id}
                onPress={() => setSelectedAiFocusId(opt.id)}
                accessibilityState={{ selected }}
                accessibilityLabel={`${opt.label} plan focus${selected ? ', selected' : ''}`}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.45),
                  paddingHorizontal: theme.spacing(1.25),
                  paddingVertical: theme.spacing(0.6),
                  borderRadius: theme.radii.pill,
                  backgroundColor: selected ? theme.colors.primary + '18' : theme.colors.surface,
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? theme.colors.primary : theme.colors.borderMuted,
                  opacity: pressed ? 0.85 : 1,
                })}
              >
                {selected ? (
                  <Ionicons name="checkmark-circle" size={16} color={theme.colors.primary} />
                ) : null}
                <Caption
                  style={{
                    fontWeight: '700',
                    color: selected ? theme.colors.primary : theme.colors.text,
                  }}
                >
                  {opt.label}
                </Caption>
              </Pressable>
            );
          })}
        </View>
      </View>
    );
  }, [
    aiPlannerIntentAppendixRef,
    onGenerateSuggestions,
    selectedAiFocusId,
    theme.colors.borderMuted,
    theme.colors.primary,
    theme.colors.surface,
    theme.colors.text,
    theme.colors.textSecondary,
    theme.spacing,
    theme.radii.pill,
  ]);

  const renderAiPlanToolbar = useCallback(
    (options?: {
      generateLabel?: string;
      showGenerate?: boolean;
      onGenerate?: () => void;
    }) => {
      const showGenerate = options?.showGenerate !== false && !!onGenerateSuggestions;
      const generateLabel =
        options?.generateLabel ??
        (aiSuggestionsLoading ? 'Generating...' : 'Generate plan');

      return (
        <View style={{ gap: theme.spacing(1), width: '100%' }}>
          {planIdeaQuotaEl}
          {renderAiFocusChips()}
          {showGenerate ? (
            <Button
              label={generateLabel}
              onPress={options?.onGenerate ?? (() => triggerAi('itinerary'))}
              disabled={aiSuggestionsLoading || planIdeasQuotaBlocked}
              style={{ width: '100%' }}
            />
          ) : null}
        </View>
      );
    },
    [
      aiSuggestionsLoading,
      onGenerateSuggestions,
      planIdeaQuotaEl,
      planIdeasQuotaBlocked,
      renderAiFocusChips,
      theme.spacing,
      triggerAi,
    ],
  );

  const renderAiPlanStatus = useCallback(
    (content: {
      icon: keyof typeof Ionicons.glyphMap;
      iconColor?: string;
      message: string;
      footer?: ReactNode;
    }) => (
      <View style={{ gap: theme.spacing(0.75), width: '100%' }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(0.75) }}>
          <Ionicons
            name={content.icon}
            size={20}
            color={content.iconColor ?? theme.colors.textSecondary}
            style={{ marginTop: 2 }}
          />
          <Body color={theme.colors.textSecondary} style={{ flex: 1 }}>
            {content.message}
          </Body>
        </View>
        {content.footer}
      </View>
    ),
    [theme.colors.textSecondary],
  );

  useEffect(() => {
    if (!visible || activeTab !== 'for_you') return;

    let cancelled = false;

    const load = async () => {
      setForYouLoading(true);
      setForYouError(null);
      try {
        const travelStyles = (trip as any)?.travelStyles || [];
        const budgetContext = typeof trip.budget === 'number' ? { max: trip.budget } : undefined;

        const context: RecommendationContext = {
          tripId: tripWithOwner.id,
          userId: tripWithOwner.owner || userId || '',
          trip: tripWithOwner,
          currentLocation,
          selectedDay: currentDay,
          budget: budgetContext,
          travelStyles,
        };

        const results = await getContextualRecommendations(context);
        const filtered = results.filter((rec) => rec.source !== 'ai'); // AI has its own tab
        if (!cancelled) setForYouRecommendations(filtered);
      } catch (error: any) {
        if (!cancelled) {
          setForYouError(error?.message || 'Unable to load recommendations right now.');
          setForYouRecommendations([]);
        }
      } finally {
        if (!cancelled) setForYouLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [visible, activeTab, currentDay, currentLocation, trip, forYouRefreshToken]);

  const handleAddLocalPick = async (item: LocalPicksRecommendationItem) => {
    if (__DEV__) {
      console.log('[PlanningIdeasModal] handleAddLocalPick called', { 
        itemName: item?.name,
        hasOnAddRecommendationToItinerary: !!onAddRecommendationToItinerary 
      });
    }
    
    if (!onAddRecommendationToItinerary) {
      Alert.alert('Unavailable', 'Adding to the itinerary is not available right now.');
      return;
    }

    setAddingLocalPicksId(item.id);
    try {
      const tripStart = safeTripStartDate ? new Date(safeTripStartDate) : new Date();
      tripStart.setHours(0, 0, 0, 0);
      const start = currentDay
        ? new Date(tripStart.getTime() + (currentDay - 1) * 24 * 60 * 60 * 1000)
        : new Date(tripStart);
      const picked = await promptForTripDateTime(item.name, start, { dismissIdeas: false });
      if (!picked) return;

      const optimisticKey = `localPicks:${item.id}`;
      // Perceived latency fix: mark as added immediately after user confirms date/time.
      setAddedItineraryKeys((prev) => new Set(prev).add(optimisticKey));
      setAddingLocalPicksId(null);

      const coercedDate = coerceDate(picked.date) ?? safeTripStartDate ?? new Date();
      try {
        await onAddRecommendationToItinerary({
          title: item.name,
          description: stripHtmlTags(item.description || ''),
          category: inferLocalPickCategory(item.type, item.name),
          startDate: coercedDate,
          startTime: picked.startTime,
          endTime: picked.endTime,
          address: item.location?.address || undefined,
          location: item.location?.address || item.name || undefined,
        });
      } catch (saveError) {
        // Roll back optimistic state if persistence fails.
        setAddedItineraryKeys((prev) => {
          const next = new Set(prev);
          next.delete(optimisticKey);
          return next;
        });
        throw saveError;
      }
    } catch (error: any) {
      if (isStagingDebug) {
        console.log('[Ideas:AddToItinerary] LocalPick failed', {
          tripId: trip?.id,
          itemId: item?.id,
          itemName: item?.name,
          currentDay,
          safeTripStartDate: safeTripStartDate?.toISOString?.(),
          safeTripEndDate: safeTripEndDate?.toISOString?.(),
          errorMessage: error?.message,
          errorStack: error?.stack,
        });
      }
      const isOffline = error?.code === 'unavailable' || error?.code === 'failed-precondition';
      Alert.alert(
        'Unable to add',
        isOffline
          ? "You're offline. Check your connection and try again."
          : error?.message || 'Please try again in a moment.',
      );
    } finally {
      setAddingLocalPicksId(null);
    }
  };

  const setMaybeSaving = (id: string, saving: boolean) => {
    setSavingMaybeIds((prev) => {
      const next = new Set(prev);
      if (saving) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleAddToMaybe = async (input: {
    id: string;
    title: string;
    description?: string;
    source: 'ai' | 'recommendation';
    sourceId?: string;
  }) => {
    if (!userId) {
      Alert.alert('Sign in required', 'Please sign in to save ideas to Maybe.');
      return;
    }
    if (!trip?.id) {
      showError('Trip not found. Please close and try again.');
      return;
    }
    const title = input.title.trim();
    if (!title) return;
    setMaybeSaving(input.id, true);
    try {
      showInlineToast('Saving to Maybe…', 'info', 2400);
      if (!networkMonitor.isOnline()) {
        showInlineToast('You are offline. Saving to Maybe may fail until you reconnect.', 'info', 4500);
      }

      const writePromise = createMaybeItem(trip.id, {
        title,
        description: input.description,
        source: input.source,
        sourceId: input.sourceId,
        tags: [],
        createdBy: userId,
      });
      const syncHintTimer = setTimeout(() => {
        showInlineToast('Syncing…', 'info', 4000);
      }, 1500);
      await writePromise;
      clearTimeout(syncHintTimer);
      showInlineToast('Added to Maybe', 'success', 3500);
      onMaybeAdded?.();
    } catch (error: any) {
      showInlineToast(error?.message || 'Unable to save to Maybe right now.', 'error', 4500);
    } finally {
      setMaybeSaving(input.id, false);
    }
  };

  const openBookingLink = (link?: string) => {
    if (!link) {
      return;
    }
    Linking.openURL(link).catch((error) => {
      logger.error('Failed to open booking link', error instanceof Error ? error : new Error(String(error)));
      Alert.alert('Error', 'Unable to open the booking link right now.');
    });
  };

  const renderAiTab = () => {
    if (aiRequestMode !== 'itinerary') {
      return (
        <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1), width: '100%' }}>
          {renderAiPlanToolbar()}
          {renderAiPlanStatus({
            icon: 'sparkles-outline',
            iconColor: theme.colors.primary,
            message: 'Generate a day-by-day draft plan tailored to your trip.',
          })}
        </View>
      );
    }

    const isLoadingThisTab = !!aiSuggestionsLoading && aiSuggestionsRequestMode === 'itinerary';
    const modeSuggestions = aiSuggestionsByMode.itinerary;
    const showUpdatingBanner = isLoadingThisTab && !!modeSuggestions;

    if (isLoadingThisTab && !modeSuggestions) {
      return (
        <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1), width: '100%' }}>
          {renderAiPlanToolbar({ generateLabel: 'Generating...' })}
          {renderAiPlanStatus({
            icon: 'sparkles',
            iconColor: theme.colors.primary,
            message: aiSuggestionsLoadingExtended
              ? 'Still working — this can take a bit longer.'
              : 'Hang tight, we’re building a plan for your trip.',
            footer: (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
                {onMinimizeIdeas ? (
                  <Button label="Minimize" variant="ghost" size="small" onPress={onMinimizeIdeas} />
                ) : null}
                {onCancelGenerateSuggestions ? (
                  <Button label="Cancel" variant="secondary" size="small" onPress={onCancelGenerateSuggestions} />
                ) : null}
              </View>
            ),
          })}
          {Array.from({ length: 3 }).map((_, idx) => (
            <Card key={`ai-skel-${idx}`} variant="outlined" style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
              <SkeletonLoader width="70%" height={18} borderRadius={theme.radii.sm} />
              <SkeletonLoader width="92%" height={14} borderRadius={theme.radii.sm} />
              <SkeletonLoader width="82%" height={14} borderRadius={theme.radii.sm} />
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(0.25) }}>
                <SkeletonLoader width="50%" height={40} borderRadius={theme.radii.lg} />
                <SkeletonLoader width="50%" height={40} borderRadius={theme.radii.lg} />
              </View>
            </Card>
          ))}
        </View>
      );
    }

    // Filter suggestions to remove duplicates (guard: modeSuggestions.suggestions may be undefined)
    const rawSuggestions = modeSuggestions?.suggestions;
    const filteredSuggestions =
      Array.isArray(rawSuggestions)
        ? filterDuplicateSuggestions(rawSuggestions, safeItineraryItems)
        : [];

    const addAiSuggestionWithPrompt = async (suggestion: any) => {
      if (__DEV__) {
        console.log('[PlanningIdeasModal] addAiSuggestionWithPrompt called', { 
          suggestionTitle: suggestion?.title,
          hasOnAddSingleSuggestion: !!onAddSingleSuggestion 
        });
      }
      
      if (!onAddSingleSuggestion) {
        // Show user feedback instead of silently returning
        Alert.alert('Unavailable', 'Adding to the itinerary is not available right now. Please try again later.');
        return;
      }
      
      const suggestionKey = `ai:${suggestion.day}-${suggestion.title}`;
      if (addedItineraryKeys.has(suggestionKey)) {
        if (__DEV__) {
          console.log('[PlanningIdeasModal] Suggestion already added', { suggestionKey });
        }
        return;
      }
      
      const defaultDate = (() => {
        if (Array.isArray(tripDates) && tripDates.length > 0) {
          const idx = typeof suggestion.day === 'number' && suggestion.day > 0
            ? Math.min(suggestion.day - 1, tripDates.length - 1)
            : 0;
          return tripDates[idx];
        }
        return undefined;
      })();

      const picked = await promptForTripDateTime(
        suggestion.title || 'Add to itinerary',
        defaultDate || null,
      );
      if (!picked) {
        if (__DEV__) {
          console.log('[PlanningIdeasModal] User cancelled date/time selection');
        }
        return;
      }
      
      try {
        if (__DEV__) {
          console.log('[PlanningIdeasModal] Calling onAddSingleSuggestion', { 
            suggestionTitle: suggestion?.title,
            pickedDate: picked?.date?.toISOString?.(),
          });
        }
        await Promise.resolve(onAddSingleSuggestion(suggestion, picked) as any);
        setAddedItineraryKeys((prev) => new Set(prev).add(suggestionKey));
        if (__DEV__) {
          console.log('[PlanningIdeasModal] Successfully added suggestion');
        }
      } catch (error: any) {
        if (isStagingDebug || __DEV__) {
          console.log('[Ideas:AddToItinerary] AI suggestion failed', {
            tripId: trip?.id,
            suggestionTitle: suggestion?.title,
            suggestionDay: suggestion?.day,
            pickedDate: picked?.date?.toISOString?.(),
            errorMessage: error?.message,
            errorStack: error?.stack,
          });
        }
        showError(error?.message || 'Unable to add to itinerary right now.');
      }
    };

    if (!modeSuggestions || filteredSuggestions.length === 0) {
      const showTimeoutHint =
        aiSuggestionsLoading === false &&
        (!modeSuggestions || (modeSuggestions.suggestions?.length ?? 0) === 0);
      const showErrorState =
        !!aiSuggestionsError && aiSuggestionsErrorMode === 'itinerary' && (aiSuggestionsLoading !== true);
      const showInlineGenerateCta = !!onGenerateSuggestions && !itinerarySuggestionsStale;
      return (
        <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1), width: '100%' }}>
          {renderAiPlanToolbar({
            showGenerate: showInlineGenerateCta,
            generateLabel: showErrorState ? 'Try again' : undefined,
          })}
          {renderAiPlanStatus({
            icon: showErrorState ? 'alert-circle-outline' : 'sparkles',
            iconColor: showErrorState ? theme.colors.error : theme.colors.primary,
            message: showErrorState
              ? aiSuggestionsError || 'Unable to generate a plan right now.'
              : modeSuggestions && modeSuggestions.suggestions?.length > 0 && filteredSuggestions.length === 0
                ? 'All suggestions have already been added to your itinerary.'
                : 'Ask Tripvio AI to create a day-by-day plan tailored to this itinerary.',
            footer: showTimeoutHint ? (
              <Caption color={theme.colors.textSecondary}>
                If this keeps timing out or says unavailable, confirm the Functions host is reachable and your AI key isn’t rate limited.
              </Caption>
            ) : undefined,
          })}
        </View>
      );
    }

    const { shouldGroup: shouldGroupAiByCity, sections: aiCitySections } = buildAiCitySections(
      filteredSuggestions,
      trip.destinations,
    );

    return (
      <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1) }}>
        {renderAiPlanToolbar({
          generateLabel: aiSuggestionsLoading ? 'Generating...' : 'Generate plan',
        })}
        {showUpdatingBanner ? (
          <Card variant="outlined" style={{ padding: theme.spacing(1.5) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="sparkles" size={18} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong">Updating suggestions…</ThemedText>
                </View>
                <Caption color={theme.colors.textSecondary}>
                  {aiSuggestionsLoadingExtended ? 'Still working — this can take a bit longer.' : 'Hang tight, we’re refreshing your plan.'}
                </Caption>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: theme.spacing(0.75),
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {onMinimizeIdeas ? (
                  <Button label="Minimize" variant="ghost" size="small" onPress={onMinimizeIdeas} />
                ) : null}
                {onCancelGenerateSuggestions ? (
                  <Button label="Cancel" variant="secondary" size="small" onPress={onCancelGenerateSuggestions} />
                ) : null}
              </View>
            </View>
          </Card>
        ) : null}
        {(() => {
          const isCached = (modeSuggestions?.debug as any)?.cached === true;
          return isCached ? (
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              Showing cached suggestions.
            </Caption>
          ) : null;
        })()}
        {modeSuggestions?.summary && (
          <Body color={theme.colors.textSecondary}>{modeSuggestions.summary}</Body>
        )}
        {aiCitySections.map((section) => (
          <View key={section.key} style={{ gap: theme.spacing(1.5) }}>
            {shouldGroupAiByCity && section.label ? (
              <SubHeading style={{ marginTop: theme.spacing(0.5) }}>{section.label}</SubHeading>
            ) : null}
            {section.items.map((suggestion: any, index: number) => {
              const suggestionId = `${suggestion.day}-${suggestion.title}`;
              const isAdding = addingSuggestionIds.has(suggestionId);
              const isAdded = addedItineraryKeys.has(`ai:${suggestionId}`);
              const destHint = suggestion.city || trip.destination || trip.name;
              if (EnhancedSuggestionCard && mapToEnhancedSuggestion) {
                const maybeId = `ai-${section.key}-${suggestionId}-${index}`;
                const isSavingMaybe = savingMaybeIds.has(maybeId);
                const enhancedSuggestion = mapToEnhancedSuggestion(suggestion, destHint);
                return (
                  <EnhancedSuggestionCard
                    key={`${section.key}-${suggestion.day}-${suggestion.title}-${index}`}
                    suggestion={enhancedSuggestion}
                    showImage
                    onApply={isAdding || isAdded ? undefined : () => addAiSuggestionWithPrompt(suggestion)}
                    onSaveMaybe={() =>
                      handleAddToMaybe({
                        id: maybeId,
                        title: suggestion.title || enhancedSuggestion.title || 'Idea',
                        description: suggestion.description || enhancedSuggestion.description,
                        source: 'ai',
                        sourceId: suggestionId,
                      })
                    }
                    isSavingMaybe={isSavingMaybe}
                    isAdding={isAdding}
                    isAdded={isAdded}
                    tripId={trip.id}
                  />
                );
              }
              const maybeId = `ai-${section.key}-${suggestionId}`;
              const isSavingMaybe = savingMaybeIds.has(maybeId);
              const categoryLabel = formatCategoryLabel(suggestion.category);
              return (
                <Card
                  key={`${section.key}-${suggestionId}`}
                  variant="outlined"
                  style={{ padding: theme.spacing(1.5) }}
                >
                  <ThemedText variant="bodyStrong">{suggestion.title}</ThemedText>
                  {categoryLabel ? (
                    <View
                      style={{
                        marginTop: theme.spacing(0.5),
                        alignSelf: 'flex-start',
                        paddingHorizontal: theme.spacing(1),
                        paddingVertical: theme.spacing(0.35),
                        borderRadius: theme.radii.pill,
                        backgroundColor: theme.colors.primary + '12',
                        borderWidth: 1,
                        borderColor: theme.colors.primary + '25',
                      }}
                    >
                      <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
                        {categoryLabel}
                      </Caption>
                    </View>
                  ) : null}
                  <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                    {suggestion.description}
                  </Body>
                  <Caption color={theme.colors.textSecondary}>
                    Day {suggestion.day}
                    {suggestion.city ? ` · ${suggestion.city}` : ''}
                  </Caption>
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(1) }}>
                    {onAddSingleSuggestion && (
                      <Button
                        label={isAdded ? 'Added ✓' : isAdding ? 'Adding...' : 'Add to itinerary'}
                        onPress={() => addAiSuggestionWithPrompt(suggestion)}
                        disabled={isAdding || isAdded}
                        style={{ flex: 1 }}
                      />
                    )}
                    <Button
                      label={isSavingMaybe ? 'Saving...' : 'Add to Maybe'}
                      variant="secondary"
                      onPress={() =>
                        handleAddToMaybe({
                          id: maybeId,
                          title: suggestion.title || 'Idea',
                          description: suggestion.description,
                          source: 'ai',
                          sourceId: suggestionId,
                        })
                      }
                      disabled={isSavingMaybe}
                      style={{ flex: 1 }}
                      icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
                    />
                  </View>
                </Card>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  const renderPackagesTab = () => {
    if (aiRequestMode !== 'packages') {
      return (
        <View
          style={{
            alignItems: 'center',
            paddingTop: theme.spacing(4),
            gap: theme.spacing(1.5),
            width: '100%',
          }}
        >
          {planIdeaQuotaEl}
          <Ionicons name="pricetags-outline" size={28} color={theme.colors.primary} />
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Get AI suggestions for city passes and bundled tickets (e.g., CityPASS, Go City, museum passes).
          </Body>
          {onGenerateSuggestions && (
            <Button
              label={aiSuggestionsLoading ? 'Generating...' : 'Generate packages'}
              onPress={() => {
                triggerAi('packages');
              }}
              disabled={aiSuggestionsLoading || planIdeasQuotaBlocked}
              style={{ width: '100%' }}
            />
          )}
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Tip: We’ll include “Buy” links when we’re confident about the official page.
          </Caption>
        </View>
      );
    }

    const isLoadingThisTab = !!aiSuggestionsLoading && aiSuggestionsRequestMode === 'packages';
    const modeSuggestions = aiSuggestionsByMode.packages;
    const showUpdatingBanner = isLoadingThisTab && !!modeSuggestions;

    if (isLoadingThisTab && !modeSuggestions) {
      return (
        <View style={{ gap: theme.spacing(1.25), paddingTop: theme.spacing(2), width: '100%' }}>
          {planIdeaQuotaEl}
          <View style={{ alignItems: 'center', gap: theme.spacing(0.75) }}>
            <Ionicons name="pricetags-outline" size={24} color={theme.colors.primary} />
            <ThemedText variant="bodyStrong">Generating packages…</ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              {aiSuggestionsLoadingExtended ? 'Still working — this can take a bit longer.' : 'Finding the best bundles and passes for your trip.'}
            </Caption>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75), justifyContent: 'center' }}>
              {onMinimizeIdeas ? (
                <Button label="Minimize" variant="ghost" size="small" onPress={onMinimizeIdeas} />
              ) : null}
              {onCancelGenerateSuggestions ? (
                <Button label="Cancel" variant="secondary" size="small" onPress={onCancelGenerateSuggestions} />
              ) : null}
            </View>
          </View>
          {Array.from({ length: 3 }).map((_, idx) => (
            <Card key={`pkg-skel-${idx}`} variant="outlined" style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
              <SkeletonLoader width="60%" height={18} borderRadius={theme.radii.sm} />
              <SkeletonLoader width="94%" height={14} borderRadius={theme.radii.sm} />
              <SkeletonLoader width="78%" height={14} borderRadius={theme.radii.sm} />
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(0.25) }}>
                <SkeletonLoader width="50%" height={40} borderRadius={theme.radii.lg} />
                <SkeletonLoader width="50%" height={40} borderRadius={theme.radii.lg} />
              </View>
            </Card>
          ))}
        </View>
      );
    }

    const rawPkgSuggestions = modeSuggestions?.suggestions;
    const suggestions = Array.isArray(rawPkgSuggestions) ? rawPkgSuggestions : [];
    const packageSuggestions = suggestions.filter((s) => s.isPackage === true);
    if (!modeSuggestions || suggestions.length === 0) {
      const showErrorState =
        !!aiSuggestionsError && aiSuggestionsErrorMode === 'packages' && (aiSuggestionsLoading !== true);
      return (
        <View
          style={{
            alignItems: 'center',
            paddingTop: theme.spacing(4),
            gap: theme.spacing(1.5),
            width: '100%',
          }}
        >
          {planIdeaQuotaEl}
          <Ionicons name={showErrorState ? 'alert-circle-outline' : 'pricetags-outline'} size={28} color={showErrorState ? theme.colors.error : theme.colors.primary} />
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            {showErrorState ? aiSuggestionsError : 'No package suggestions yet.'}
          </Body>
          {onGenerateSuggestions && (
            <Button
              label={showErrorState ? 'Try again' : 'Generate packages'}
              onPress={() => {
                triggerAi('packages');
              }}
              disabled={planIdeasQuotaBlocked}
              style={{ width: '100%' }}
            />
          )}
        </View>
      );
    }

    if (packageSuggestions.length === 0) {
      return (
        <View
          style={{
            alignItems: 'center',
            paddingTop: theme.spacing(4),
            gap: theme.spacing(1.5),
            width: '100%',
          }}
        >
          {planIdeaQuotaEl}
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            No multi-activity packages found in the latest results.
          </Body>
          {onGenerateSuggestions && (
            <Button
              label="Regenerate packages"
              onPress={() => {
                triggerAi('packages');
              }}
              disabled={planIdeasQuotaBlocked}
              style={{ width: '100%' }}
            />
          )}
        </View>
      );
    }

    const addPackageReminder = async (s: ChatPlannerSuggestion) => {
      if (__DEV__) {
        console.log('[PlanningIdeasModal] addPackageReminder called', { 
          title: s?.title,
          hasOnAddRecommendationToItinerary: !!onAddRecommendationToItinerary 
        });
      }
      
      if (!onAddRecommendationToItinerary) {
        Alert.alert('Unavailable', 'Adding to the itinerary is not available right now.');
        return;
      }
      const key = `packages:${s.title || ''}:${s.description || ''}`;
      if (addedItineraryKeys.has(key)) return;
      try {
        const tripStart = safeTripStartDate ? new Date(safeTripStartDate) : new Date();
        tripStart.setHours(0, 0, 0, 0);
        const start = currentDay
          ? new Date(tripStart.getTime() + (currentDay - 1) * 24 * 60 * 60 * 1000)
          : new Date(tripStart);
        const picked = await promptForTripDateTime(s.title || 'Add to itinerary', start);
        if (!picked) return;
        const coercedDate = coerceDate(picked.date) ?? safeTripStartDate ?? new Date();
        await onAddRecommendationToItinerary({
          title: s.title || 'City pass',
          description: s.description,
          category: 'activity',
          startDate: coercedDate,
          startTime: picked.startTime,
          endTime: picked.endTime,
          location: trip.destination || trip.name,
        });
        setAddedItineraryKeys((prev) => new Set(prev).add(key));
      } catch (error: any) {
        if (isStagingDebug) {
          console.log('[Ideas:AddToItinerary] Package reminder failed', {
            tripId: trip?.id,
            title: s?.title,
            pickedDay: currentDay,
            safeTripStartDate: safeTripStartDate?.toISOString?.(),
            errorMessage: error?.message,
            errorStack: error?.stack,
          });
        }
        Alert.alert('Unable to add', error?.message || 'Please try again in a moment.');
      }
    };

    return (
      <View style={{ gap: theme.spacing(1.5) }}>
        {planIdeaQuotaEl}
        {showUpdatingBanner ? (
          <Card variant="outlined" style={{ padding: theme.spacing(1.5) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: theme.spacing(1),
              }}
            >
              <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                  <Ionicons name="pricetags-outline" size={18} color={theme.colors.primary} />
                  <ThemedText variant="bodyStrong">Updating packages…</ThemedText>
                </View>
                <Caption color={theme.colors.textSecondary}>
                  {aiSuggestionsLoadingExtended ? 'Still working — this can take a bit longer.' : 'Hang tight, we’re refreshing your results.'}
                </Caption>
              </View>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: theme.spacing(0.75),
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {onMinimizeIdeas ? (
                  <Button label="Minimize" variant="ghost" size="small" onPress={onMinimizeIdeas} />
                ) : null}
                {onCancelGenerateSuggestions ? (
                  <Button label="Cancel" variant="secondary" size="small" onPress={onCancelGenerateSuggestions} />
                ) : null}
              </View>
            </View>
          </Card>
        ) : null}
        {(() => {
          const isCached = (modeSuggestions?.debug as any)?.cached === true;
          return isCached ? (
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              Showing cached suggestions.
            </Caption>
          ) : null;
        })()}
        {modeSuggestions.summary ? (
          <Body color={theme.colors.textSecondary}>{modeSuggestions.summary}</Body>
        ) : null}
        {packageSuggestions.map((s, idx) => {
          const link = (s as any)?.ticketLink as string | undefined;
          const maybeId = `package-${idx}-${s.title || 'idea'}`;
          const isSavingMaybe = savingMaybeIds.has(maybeId);
          const isAdded = addedItineraryKeys.has(`packages:${s.title || ''}:${s.description || ''}`);
          return (
            <Card key={`${s.title}-${idx}`} variant="outlined" style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
              <ThemedText variant="bodyStrong">{s.title}</ThemedText>
              {s.description ? (
                <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                  {s.description}
                </Body>
              ) : null}
              <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                <Button
                  label={isAdded ? 'Added ✓' : 'Add reminder'}
                  onPress={() => addPackageReminder(s)}
                  disabled={isAdded}
                  style={{ flex: 1 }}
                />
                {link ? (
                  <Button
                    label="Buy"
                    variant="secondary"
                    onPress={() => openBookingLink(link)}
                    style={{ flex: 1 }}
                  />
                ) : null}
              </View>
              <Button
                label={isSavingMaybe ? 'Saving...' : 'Add to Maybe'}
                variant="secondary"
                onPress={() =>
                  handleAddToMaybe({
                    id: maybeId,
                    title: s.title || 'Package idea',
                    description: s.description,
                    source: 'ai',
                    sourceId: maybeId,
                  })
                }
                disabled={isSavingMaybe}
                icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
              />
            </Card>
          );
        })}
      </View>
    );
  };

  const renderLocalPicksTab = () => {
    if (localPicksLoading) {
      return (
        <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1), width: '100%' }}>
          <View style={{ alignItems: 'center', gap: theme.spacing(0.75), marginBottom: theme.spacing(0.25) }}>
            <Ionicons name="sparkles" size={22} color={theme.colors.primary} />
            <ThemedText variant="bodyStrong" style={{ textAlign: 'center' }}>
              {localPicksDestinationHint
                ? `Finding spots near ${localPicksDestinationHint}`
                : 'Finding local picks…'}
            </ThemedText>
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingHorizontal: theme.spacing(1) }}>
              Usually takes a few seconds. We’re loading landmarks, dining, and cafés for your trip.
            </Caption>
          </View>
          {Array.from({ length: LOCAL_PICKS_SKELETON_COUNT }).map((_, idx) => (
            <Card
              key={`local-picks-skel-${idx}`}
              variant="outlined"
              style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}
            >
              <SkeletonLoader width="100%" height={140} borderRadius={14} />
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flexWrap: 'wrap' }}>
                <SkeletonLoader width={72} height={14} borderRadius={theme.radii.pill} />
                <SkeletonLoader width={56} height={14} borderRadius={theme.radii.pill} />
                <SkeletonLoader width="42%" height={14} borderRadius={theme.radii.sm} />
              </View>
              <SkeletonLoader width="78%" height={20} borderRadius={theme.radii.sm} />
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                <SkeletonLoader width={14} height={14} borderRadius={4} />
                <SkeletonLoader width="88%" height={14} borderRadius={theme.radii.sm} />
              </View>
              <SkeletonLoader width="100%" height={14} borderRadius={theme.radii.sm} />
              <SkeletonLoader width="64%" height={14} borderRadius={theme.radii.sm} />
              <SkeletonLoader width={56} height={14} borderRadius={theme.radii.sm} />
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), marginTop: theme.spacing(0.25) }}>
                <SkeletonLoader width="50%" height={44} borderRadius={theme.radii.lg} />
                <SkeletonLoader width="50%" height={44} borderRadius={theme.radii.lg} />
              </View>
              <SkeletonLoader width="100%" height={44} borderRadius={theme.radii.lg} />
            </Card>
          ))}
        </View>
      );
    }

    if (localPicksError) {
      const isConfigError =
        /not configured/i.test(localPicksError) ||
        /failed-precondition/i.test(localPicksError) ||
        /api key/i.test(localPicksError);
      return (
        <View style={{ alignItems: 'center', paddingTop: theme.spacing(4), gap: theme.spacing(1.5) }}>
          <Ionicons
            name={isConfigError ? 'construct-outline' : 'alert-circle-outline'}
            size={28}
            color={isConfigError ? theme.colors.textSecondary : theme.colors.error}
          />
          <ThemedText variant="bodyStrong" style={{ textAlign: 'center' }}>
            {isConfigError ? 'Local picks not available in this environment' : 'Unable to load local picks'}
          </ThemedText>
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            {localPicksError}
          </Caption>
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            <Button label="Generate local picks" onPress={onRefreshLocalPicks} size="small" />
            <Button label="Try AI plan" onPress={() => onTabChange('ai')} size="small" variant="secondary" />
          </View>
        </View>
      );
    }

    if (!safeLocalPicksRecommendations.length) {
      return (
        <View style={{ alignItems: 'center', paddingTop: theme.spacing(4), gap: theme.spacing(1.5) }}>
          <Ionicons name="sparkles-outline" size={28} color={theme.colors.textSecondary} />
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            No local recommendations yet.
          </Body>
          {localPicksDestinationHint ? (
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingHorizontal: theme.spacing(2) }}>
              We’ll use {localPicksDestinationHint} to find nearby highlights, dining, and cafés.
            </Caption>
          ) : null}
          <Button
            label="Generate local picks"
            onPress={onRefreshLocalPicks}
            size="small"
            style={{ minWidth: 200 }}
          />
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center', paddingHorizontal: theme.spacing(2) }}>
            {localPicksDestinationHint
              ? 'First load may take a few seconds; results are cached for the day.'
              : 'Add a destination to your trip for the most accurate picks.'}
          </Caption>
        </View>
      );
    }

    return (
      <View style={{ gap: theme.spacing(1.5) }}>
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
          <Button
            label="Refresh"
            variant="secondary"
            size="small"
            onPress={onRefreshLocalPicks}
          />
        </View>
        {visibleLocalPicks.map((item) => {
          const address =
            item.location?.address ||
            (item.location as any)?.name ||
            String((item as any)?.metadata?.address || '').trim() ||
            '';
          const snippetRaw = item.description ? stripHtmlTags(item.description).trim() : '';
          const normalize = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
          const normAddress = address ? normalize(address) : '';
          const normSnippet = snippetRaw ? normalize(snippetRaw) : '';
          const isSnippetDuplicateAddress =
            !!normSnippet && !!normAddress && (normSnippet === normAddress || normSnippet.includes(normAddress));
          const fallbackSnippetByType: Record<LocalPicksRecommendationItem['type'], string> = {
            poi: 'A point of interest worth checking out.',
            activity: 'An activity you can add to your day.',
            hotel: 'A stay option to consider for this trip.',
            flight: 'A flight option you can review and add.',
          };
          const snippet = !snippetRaw || isSnippetDuplicateAddress ? fallbackSnippetByType[item.type] : snippetRaw;
          const maybeId = `localPicks-${item.id}`;
          const isSavingMaybe = savingMaybeIds.has(maybeId);

          return (
            <Card key={item.id} variant="outlined" style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
            {(() => {
              const imageUri = getRecommendationImageUrl(item);
              return imageUri ? (
                <Image
                  source={{ uri: imageUri }}
                  resizeMode="cover"
                  style={{
                    width: '100%',
                    height: 140,
                    borderRadius: 14,
                    backgroundColor: theme.colors.borderMuted,
                  }}
                />
              ) : null;
            })()}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), flexWrap: 'wrap', minWidth: 0 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name={getTypeIcon(item.type)} size={14} color={theme.colors.primary} />
                <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flexShrink: 0 }}>
                  {getTypeLabel(item.type)}
                </Caption>
              </View>
              {(() => {
                const categoryLabel = getCategoryLabel(item);
                const tone = getCategoryTone(categoryLabel);
                return categoryLabel ? (
                  <View
                    style={{
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.35),
                      borderRadius: theme.radii.pill,
                      backgroundColor: (tone?.color || theme.colors.primary) + 'E6',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                    }}
                  >
                    {tone ? <Ionicons name={tone.icon} size={12} color="#fff" /> : null}
                    <Caption color="#fff" style={{ fontWeight: '700' }}>
                      {categoryLabel}
                    </Caption>
                  </View>
                ) : null;
              })()}
              {(item as any)?.metadata?.city ? (
                <>
                  <Caption color={theme.colors.textSecondary}>•</Caption>
                  <Caption
                    color={theme.colors.textSecondary}
                    numberOfLines={1}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    {String((item as any).metadata.city)}
                  </Caption>
                </>
              ) : null}
            </View>
            <ThemedText variant="bodyStrong" numberOfLines={2} style={{ minWidth: 0 }}>
              {item.name}
            </ThemedText>
            {address ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flex: 1 }}>
                  {address}
                </Caption>
              </View>
            ) : null}
            {snippet ? (
              <Caption color={theme.colors.textSecondary} numberOfLines={3}>
                {snippet}
              </Caption>
            ) : null}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
              {item.rating && (
                <Caption color={theme.colors.textSecondary}>
                  ⭐️ {item.rating.toFixed(1)}
                </Caption>
              )}
              {item.price && (
                <Caption color={theme.colors.primary}>
                  {item.price.currency} {item.price.amount.toLocaleString()}
                </Caption>
              )}
            </View>
            <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
              {onAddRecommendationToItinerary && (
                <Button
                  label={
                    addedItineraryKeys.has(`localPicks:${item.id}`)
                      ? 'Added ✓'
                      : addingLocalPicksId === item.id
                        ? 'Adding...'
                        : 'Add to itinerary'
                  }
                  onPress={() => handleAddLocalPick(item)}
                  disabled={addingLocalPicksId === item.id || addedItineraryKeys.has(`localPicks:${item.id}`)}
                  style={{ flex: 1 }}
                />
              )}
              {item.bookingLink && (
                <Button
                  label="Book"
                  variant="secondary"
                  onPress={() => openBookingLink(item.bookingLink)}
                  style={{ flex: 1 }}
                />
              )}
            </View>
            <Button
              label={isSavingMaybe ? 'Saving...' : 'Add to Maybe'}
              variant="secondary"
              onPress={() =>
                handleAddToMaybe({
                  id: maybeId,
                  title: item.name,
                  description: snippet,
                  source: 'recommendation',
                  sourceId: item.id,
                })
              }
              disabled={isSavingMaybe}
              icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
            />
          </Card>
          );
        })}

        {/* Expand/collapse without extra API calls (uses already-fetched + cached results) */}
        {orderedLocalPicks.length > LOCAL_PICKS_INITIAL_LIMIT && (
          <View style={{ paddingTop: theme.spacing(1) }}>
            <Button
              label={
                localPicksExpanded
                  ? 'Show less'
                  : `Show more (${orderedLocalPicks.length - LOCAL_PICKS_INITIAL_LIMIT})`
              }
              variant="secondary"
              onPress={() => setLocalPicksExpanded((v) => !v)}
            />
          </View>
        )}
      </View>
    );
  };

  const renderForYouTab = () => {
    if (forYouLoading) {
      return (
        <View style={{ alignItems: 'center', paddingTop: theme.spacing(4), gap: theme.spacing(1) }}>
          <Ionicons name="hourglass-outline" size={28} color={theme.colors.textSecondary} />
          <Body color={theme.colors.textSecondary}>Loading personalized picks...</Body>
        </View>
      );
    }

    if (forYouError) {
      const isTimeout =
        /deadline|timeout|unavailable/i.test(forYouError) ||
        /functions\/deadline-exceeded/i.test(forYouError);
      return (
        <View style={{ alignItems: 'center', paddingTop: theme.spacing(4), gap: theme.spacing(1.5) }}>
          <Caption color={theme.colors.error} style={{ textAlign: 'center' }}>
            {forYouError}
          </Caption>
          {isTimeout && (
            <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
              If you’re on device with the emulator, confirm it’s running and reachable at your LAN host/5001.
            </Caption>
          )}
          <Button label="Retry" onPress={refreshForYou} size="small" />
        </View>
      );
    }

    if (!forYouRecommendations.length) {
      // Option A: fallback to Local Picks so this tab never feels empty.
      if (orderedLocalPicks.length > 0) {
        const fallback = orderedLocalPicks.slice(0, 8);
        return (
          <View style={{ gap: theme.spacing(1.5), paddingTop: theme.spacing(1) }}>
            <Card
              variant="outlined"
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.primary + '08',
                borderColor: theme.colors.primary + '25',
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(1) }}>
                <View style={{ flex: 1, gap: theme.spacing(0.25) }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                    <Ionicons name="trending-up-outline" size={18} color={theme.colors.primary} />
                    <ThemedText variant="bodyStrong">Trending right now</ThemedText>
                  </View>
                  <Caption color={theme.colors.textSecondary}>
                    We’ll personalize this as you add items, save places, and travel more. For now, here are strong local picks.
                  </Caption>
                </View>
                <Button
                  label="See all"
                  size="small"
                  variant="secondary"
                  onPress={() => onTabChange('localPicks')}
                />
              </View>
            </Card>

            <View style={{ gap: theme.spacing(1.25) }}>
              {fallback.map((item) => {
                const address =
                  item.location?.address ||
                  (item.location as any)?.name ||
                  String((item as any)?.metadata?.address || '').trim() ||
                  '';
                const maybeId = `for-you-fallback-${item.id}`;
                const isSavingMaybe = savingMaybeIds.has(maybeId);
                return (
                  <Card key={`for-you-fallback-${item.id}`} variant="outlined" style={{ padding: theme.spacing(1.5), gap: theme.spacing(1) }}>
                  {(() => {
                    const imageUri = getRecommendationImageUrl(item);
                    return imageUri ? (
                      <Image
                        source={{ uri: imageUri }}
                        resizeMode="cover"
                        style={{
                          width: '100%',
                          height: 140,
                          borderRadius: 14,
                          backgroundColor: theme.colors.borderMuted,
                        }}
                      />
                    ) : null;
                  })()}
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), flexWrap: 'wrap', minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                      <Ionicons name={getTypeIcon(item.type)} size={14} color={theme.colors.primary} />
                      <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flexShrink: 0 }}>
                        {getTypeLabel(item.type)}
                      </Caption>
                    </View>
                    {(() => {
                      const categoryLabel = getCategoryLabel(item);
                      const tone = getCategoryTone(categoryLabel);
                      return categoryLabel ? (
                        <View
                          style={{
                            paddingHorizontal: theme.spacing(1),
                            paddingVertical: theme.spacing(0.35),
                            borderRadius: theme.radii.pill,
                            backgroundColor: (tone?.color || theme.colors.primary) + 'E6',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: theme.spacing(0.5),
                          }}
                        >
                          {tone ? <Ionicons name={tone.icon} size={12} color="#fff" /> : null}
                          <Caption color="#fff" style={{ fontWeight: '700' }}>
                            {categoryLabel}
                          </Caption>
                        </View>
                      ) : null;
                    })()}
                    {(item as any)?.metadata?.city ? (
                      <>
                        <Caption color={theme.colors.textSecondary}>•</Caption>
                        <Caption
                          color={theme.colors.textSecondary}
                          numberOfLines={1}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          {String((item as any).metadata.city)}
                        </Caption>
                      </>
                    ) : null}
                  </View>
                  <ThemedText variant="bodyStrong" numberOfLines={2} style={{ minWidth: 0 }}>
                    {item.name}
                  </ThemedText>
                  {address ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                      <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                      <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flex: 1 }}>
                        {address}
                      </Caption>
                    </View>
                  ) : null}
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                    {onAddRecommendationToItinerary && (
                      <Button
                        label={
                          addedItineraryKeys.has(`localPicks:${item.id}`)
                            ? 'Added ✓'
                            : addingLocalPicksId === item.id
                              ? 'Adding...'
                              : 'Add to itinerary'
                        }
                        onPress={() => handleAddLocalPick(item)}
                        disabled={addingLocalPicksId === item.id || addedItineraryKeys.has(`localPicks:${item.id}`)}
                        style={{ flex: 1 }}
                      />
                    )}
                    <Button
                      label="More like this"
                      variant="secondary"
                      onPress={refreshForYou}
                      style={{ flex: 1 }}
                    />
                  </View>
                  <Button
                    label={isSavingMaybe ? 'Saving...' : 'Add to Maybe'}
                    variant="secondary"
                    onPress={() =>
                      handleAddToMaybe({
                        id: maybeId,
                        title: item.name,
                        description: address || undefined,
                        source: 'recommendation',
                        sourceId: item.id,
                      })
                    }
                    disabled={isSavingMaybe}
                    icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
                  />
                </Card>
                );
              })}
            </View>
          </View>
        );
      }

      // If Local Picks are also empty, keep a friendly message.
      return (
        <View style={{ gap: theme.spacing(1.25), paddingTop: theme.spacing(2) }}>
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            No personalized recommendations yet.
          </Body>
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Add a few itinerary items or save places and we’ll tailor this. In the meantime, try Local picks or AI plan.
          </Caption>
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: theme.spacing(1) }}>
            <Button label="Local picks" onPress={() => onTabChange('localPicks')} size="small" variant="secondary" />
            <Button label="AI plan" onPress={() => onTabChange('ai')} size="small" />
          </View>
          <Button label="Retry" onPress={refreshForYou} size="small" />
        </View>
      );
    }

    // Group by source
    const grouped = new Map<PlaceRecommendation['source'], PlaceRecommendation[]>();
    forYouRecommendations.forEach((rec) => {
      if (!grouped.has(rec.source)) grouped.set(rec.source, []);
      grouped.get(rec.source)!.push(rec);
    });

    const categoryMap: Record<string, { label: string; icon: keyof typeof Ionicons.glyphMap }> = {
      similar_trips: { label: 'Similar trips', icon: 'people-outline' },
      trending: { label: 'Trending', icon: 'trending-up-outline' },
      user_history: { label: 'Based on you', icon: 'time-outline' },
      proximity: { label: 'Nearby', icon: 'location-outline' },
    };

    const categories = Array.from(grouped.entries())
      .filter(([source]) => source !== 'ai')
      .map(([source, recs]) => ({
        id: source,
        label: categoryMap[source]?.label || 'For you',
        icon: categoryMap[source]?.icon || 'sparkles-outline',
        recommendations: recs.slice(0, 6),
      }));

    return (
      <View style={{ gap: theme.spacing(2) }}>
        {categories.map((category) => (
          <View key={category.id} style={{ gap: theme.spacing(1) }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
              <Ionicons name={category.icon as any} size={16} color={theme.colors.primary} />
              <ThemedText variant="bodyStrong">{category.label}</ThemedText>
            </View>

            <View style={{ gap: theme.spacing(1) }}>
              {category.recommendations.map((rec, idx) => {
                const place = rec.place;
                const placeName =
                  place && typeof place === 'object' && 'name' in place && typeof (place as any).name === 'string'
                    ? (place as any).name
                    : 'Unknown place';
                const placeAddress =
                  place && typeof place === 'object' && 'address' in place && typeof (place as any).address === 'string'
                    ? (place as any).address
                    : undefined;
                const isPlaceRating =
                  place && typeof place === 'object' && 'averageRating' in place && 'totalRatings' in place;
                const maybeId = `for-you-${category.id}-${idx}`;
                const isSavingMaybe = savingMaybeIds.has(maybeId);
                const placeImageUri =
                  place && typeof place === 'object'
                    ? (typeof (place as any).imageUrl === 'string' && (place as any).imageUrl) ||
                      (typeof (place as any).photoUrl === 'string' && (place as any).photoUrl) ||
                      (typeof (place as any).photoUri === 'string' && (place as any).photoUri) ||
                      (typeof (place as any).image === 'string' && (place as any).image) ||
                      (typeof (place as any).photo === 'string' && (place as any).photo) ||
                      undefined
                    : undefined;

                const handleAddForYou = async () => {
                  if (__DEV__) {
                    console.log('[PlanningIdeasModal] handleAddForYou called', { 
                      placeName,
                      hasOnAddRecommendationToItinerary: !!onAddRecommendationToItinerary 
                    });
                  }
                  
                  if (!onAddRecommendationToItinerary) {
                    Alert.alert('Unavailable', 'Adding to the itinerary is not available right now.');
                    return;
                  }
                  const key = `for_you:${category.id}:${idx}`;
                  if (addedItineraryKeys.has(key)) return;
                  try {
                    const startDate = safeTripStartDate ?? new Date();
                    const itemDate = currentDay
                      ? new Date(startDate.getTime() + (currentDay - 1) * 24 * 60 * 60 * 1000)
                      : new Date();
                    const picked = await promptForTripDateTime(placeName, itemDate);
                    if (!picked) return;

                    // Infer category from place name
                    let category: ItineraryItemCategory = 'activity';
                    const lower = placeName.toLowerCase();
                    if (lower.includes('restaurant') || lower.includes('cafe')) category = 'restaurant';
                    else if (lower.includes('hotel') || lower.includes('accommodation')) category = 'hotel';
                    else if (lower.includes('museum') || lower.includes('gallery')) category = 'activity';

                    await onAddRecommendationToItinerary({
                      title: placeName,
                      description: rec.reason,
                      category,
                      startDate: picked.date,
                      startTime: picked.startTime,
                      endTime: picked.endTime,
                      address: placeAddress,
                      location: placeAddress,
                    });
                    setAddedItineraryKeys((prev) => new Set(prev).add(key));
                  } catch (error) {
                    logger.error(
                      '[PlanningIdeasModal] Error adding For you recommendation',
                      error instanceof Error ? error : new Error(String(error)),
                    );
                  }
                };

                return (
                  <View
                    key={`${category.id}-${idx}`}
                    style={{
                      padding: theme.spacing(1.5),
                      borderRadius: theme.radii.md,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                      backgroundColor: theme.colors.surface,
                      gap: theme.spacing(1),
                    }}
                  >
                    {placeImageUri ? (
                      <Image
                        source={{ uri: placeImageUri }}
                        resizeMode="cover"
                        style={{
                          width: '100%',
                          height: 140,
                          borderRadius: 14,
                          backgroundColor: theme.colors.borderMuted,
                        }}
                      />
                    ) : null}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: theme.spacing(1) }}>
                      <View style={{ flex: 1, minWidth: 0, gap: theme.spacing(0.25) }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                          <ThemedText variant="bodyStrong" numberOfLines={1} style={{ flex: 1 }}>
                            {placeName}
                          </ThemedText>
                          {isPlaceRating && (
                            <PlaceRatingBadge
                              rating={(place as any).averageRating}
                              totalRatings={(place as any).totalRatings}
                              size="small"
                            />
                          )}
                        </View>
                        {placeAddress ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                            <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                            <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ flex: 1 }}>
                              {placeAddress}
                            </Caption>
                          </View>
                        ) : null}
                        <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                          {rec.reason}
                        </Caption>
                      </View>
                    </View>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      {onAddRecommendationToItinerary && (
                        (() => {
                          const isAdded = addedItineraryKeys.has(`for_you:${category.id}:${idx}`);
                          return (
                            <Button
                              label={isAdded ? 'Added ✓' : 'Add to itinerary'}
                              onPress={handleAddForYou}
                              disabled={isAdded}
                              style={{ flex: 1 }}
                            />
                          );
                        })()
                      )}
                      <Button
                        label={isSavingMaybe ? 'Saving...' : 'Add to Maybe'}
                        variant="secondary"
                        onPress={() =>
                          handleAddToMaybe({
                            id: maybeId,
                            title: placeName,
                            description: rec.reason,
                            source: 'recommendation',
                            sourceId: `${category.id}-${idx}`,
                          })
                        }
                        disabled={isSavingMaybe}
                        style={{ flex: 1 }}
                        icon={<Ionicons name="bookmark-outline" size={18} color={theme.colors.primary} />}
                      />
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        ))}
      </View>
    );
  };

  return (
    <>
    {visible && (
    <Modal
      visible
      animationType="slide"
      presentationStyle="overFullScreen"
      statusBarTranslucent
      onRequestClose={handleIdeasModalClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.surface,
          paddingTop: insets.top + theme.spacing(1),
          paddingBottom: insets.bottom ? insets.bottom : theme.spacing(2),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(2),
          }}
        >
          <View>
            <ThemedText variant="h3" style={{ fontSize: 20, fontWeight: '700' }}>
              Ideas & AI
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>
              {ideaCount ? `${ideaCount} opportunit${ideaCount === 1 ? 'y' : 'ies'} waiting` : 'Let’s build your plan'}
            </Caption>
          </View>
          <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
            {(onGenerateSuggestions || activeTab === 'localPicks' || activeTab === 'for_you') && (
              <Pressable
                onPress={() => {
                  if (activeTab === 'localPicks') {
                    if (localPicksLoading) return;
                    onRefreshLocalPicks();
                    return;
                  }
                  if (activeTab === 'for_you') {
                    if (forYouLoading) return;
                    refreshForYou();
                    return;
                  }
                  if (!onGenerateSuggestions) return;
                  if (aiSuggestionsLoading) return;
                  const mode = activeTab === 'packages' ? 'packages' : 'itinerary';
                  triggerAi(mode);
                }}
                style={({ pressed }) => ({
                  padding: theme.spacing(1),
                  borderRadius: theme.radii.md,
                  opacity:
                    pressed ||
                      aiSuggestionsLoading ||
                      localPicksLoading ||
                      forYouLoading
                      ? 0.6
                      : 1,
                })}
              >
                <Ionicons
                  name={
                    aiSuggestionsLoading || localPicksLoading || forYouLoading
                      ? 'hourglass-outline'
                      : 'refresh-outline'
                  }
                  size={22}
                  color={theme.colors.primary}
                />
              </Pressable>
            )}
            <Pressable
              onPress={handleIdeasModalClose}
              style={({ pressed }) => ({
                padding: theme.spacing(1),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Ionicons name="close-outline" size={24} color={theme.colors.text} />
            </Pressable>
          </View>
        </View>

        {inlineToast && (
          <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(1),
                padding: theme.spacing(1.25),
                borderRadius: theme.radii.lg,
                backgroundColor:
                  inlineToast.type === 'success'
                    ? `${theme.colors.success}24`
                    : inlineToast.type === 'error'
                      ? `${theme.colors.error}1f`
                      : `${theme.colors.primary}1a`,
                borderWidth: 1,
                borderColor:
                  inlineToast.type === 'success'
                    ? `${theme.colors.success}55`
                    : inlineToast.type === 'error'
                      ? `${theme.colors.error}55`
                      : `${theme.colors.primary}4d`,
              }}
            >
              <Ionicons
                name={
                  inlineToast.type === 'success'
                    ? 'checkmark-circle-outline'
                    : inlineToast.type === 'error'
                      ? 'alert-circle-outline'
                      : 'information-circle-outline'
                }
                size={18}
                color={
                  inlineToast.type === 'success'
                    ? theme.colors.success
                    : inlineToast.type === 'error'
                      ? theme.colors.error
                      : theme.colors.primary
                }
              />
              <Body style={{ flex: 1 }}>{inlineToast.message}</Body>
              <Pressable
                onPress={() => setInlineToast(null)}
                style={({ pressed }) => ({
                  padding: 4,
                  borderRadius: theme.radii.pill,
                  opacity: pressed ? 0.6 : 1,
                })}
              >
                <Ionicons name="close" size={16} color={theme.colors.textSecondary} />
              </Pressable>
            </View>
          </View>
        )}

        <View
          style={{
            height: 1,
            backgroundColor: theme.colors.borderMuted,
            marginHorizontal: theme.spacing(2),
            marginTop: theme.spacing(1),
          }}
        />

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(1.25),
            alignItems: 'center',
          }}
          style={{ flexShrink: 0, minHeight: 52 }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            <IdeasTabButton
              label="AI plan"
              active={activeTab === 'ai'}
              count={itineraryAiCount}
              onPress={() => {
                setAiRequestMode('itinerary');
                onTabChange('ai');
              }}
            />
            <IdeasTabButton
              label="Packages"
              active={activeTab === 'packages'}
              count={packagesAiCount}
              onPress={() => {
                setAiRequestMode('packages');
                onTabChange('packages');
              }}
            />
            <IdeasTabButton
              label="Local picks"
              active={activeTab === 'localPicks'}
              count={safeLocalPicksRecommendations.length}
              onPress={() => onTabChange('localPicks')}
            />
            <IdeasTabButton
              label="For you"
              active={activeTab === 'for_you'}
              count={forYouRecommendations.length}
              onPress={() => onTabChange('for_you')}
            />
          </View>
        </ScrollView>

        {activeTab === 'localPicks' && (
          <View style={{ paddingHorizontal: theme.spacing(2), paddingBottom: theme.spacing(1) }}>
            <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ minWidth: 0 }}>
              Cached • refreshes daily
            </Caption>
          </View>
        )}

        <ScrollView
          contentContainerStyle={{
            paddingHorizontal: theme.spacing(2),
            paddingBottom: theme.spacing(3),
          }}
          showsVerticalScrollIndicator
        >
          {activeTab === 'ai' ? (
            <>
              {itineraryFocusStaleBanner}
              {renderAiTab()}
            </>
          ) : activeTab === 'packages' ? (
            renderPackagesTab()
          ) : activeTab === 'localPicks' ? (
            renderLocalPicksTab()
          ) : (
            renderForYouTab()
          )}
        </ScrollView>

        {activeTab === 'ai' &&
          onApplyAllSuggestions &&
          aiSuggestions?.suggestions?.length ? (
          <View
            style={{
              padding: theme.spacing(2),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
            }}
          >
            <Button
              label="Add all to itinerary"
              onPress={() => {
                onApplyAllSuggestions();
                onAfterApplyAll?.();
              }}
              disabled={applyingSuggestions}
            />
          </View>
        ) : null}
        {pickDayVisible && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: theme.colors.overlay,
              justifyContent: 'flex-end',
            }}
          >
            <Pressable
              style={{ flex: 1 }}
              onPress={() => {
                setPickDayVisible(false);
                pickDayResolveRef.current?.(null);
                pickDayResolveRef.current = null;
              }}
            />
            <Card
              variant="elevated"
              style={{
                borderTopLeftRadius: theme.radii.xl,
                borderTopRightRadius: theme.radii.xl,
                maxHeight: '96%',
                minHeight: '78%',
              }}
            >
              <View style={{ padding: theme.spacing(2), gap: theme.spacing(1), flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flex: 1 }}>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                      Choose a day
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                      Add “{pickDayTitle || 'this idea'}” to:
                    </Caption>
                  </View>
                  <Pressable
                    onPress={() => {
                      setPickDayVisible(false);
                      pickDayResolveRef.current?.(null);
                      pickDayResolveRef.current = null;
                    }}
                    style={({ pressed }) => ({
                      padding: theme.spacing(0.5),
                      borderRadius: theme.radii.md,
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons name="close" size={22} color={theme.colors.text} />
                  </Pressable>
                </View>

                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ paddingVertical: theme.spacing(1), gap: theme.spacing(1) }}
                  showsVerticalScrollIndicator
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                >
                  {tripDates.map((d, idx) => {
                    const isDefault =
                      !!pickDayDefault &&
                      d.getFullYear() === pickDayDefault.getFullYear() &&
                      d.getMonth() === pickDayDefault.getMonth() &&
                      d.getDate() === pickDayDefault.getDate();
                    return (
                      <Pressable
                        key={Number.isFinite(d.getTime()) ? d.toISOString() : `trip-date-${idx}`}
                        onPress={() => {
                          setPickDayVisible(false);
                          pickDayResolveRef.current?.(new Date(d));
                          pickDayResolveRef.current = null;
                        }}
                        style={({ pressed }) => ({
                          opacity: pressed ? 0.8 : 1,
                        })}
                      >
                        <View
                          style={{
                            paddingVertical: theme.spacing(1.25),
                            paddingHorizontal: theme.spacing(1.25),
                            borderRadius: theme.radii.lg,
                            borderWidth: 1,
                            borderColor: isDefault ? theme.colors.primary : theme.colors.borderMuted,
                            backgroundColor: isDefault ? theme.colors.primary + '10' : theme.colors.surface,
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <View style={{ flex: 1 }}>
                            <ThemedText variant="bodyStrong">{formatDayLabel(d)}</ThemedText>
                            <Caption color={theme.colors.textSecondary}>Day {idx + 1}</Caption>
                          </View>
                          <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
                        </View>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            </Card>
          </View>
        )}

        {/* Pick a time block (free gap) */}
        {pickTimeVisible && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: theme.colors.overlay,
              justifyContent: 'flex-end',
            }}
          >
            <Pressable
              style={{ flex: 1 }}
              onPress={() => {
                setPickTimeVisible(false);
                pickTimeResolveRef.current?.(null);
                pickTimeResolveRef.current = null;
              }}
            />
            <Card
              variant="elevated"
              style={{
                borderTopLeftRadius: theme.radii.xl,
                borderTopRightRadius: theme.radii.xl,
                maxHeight: '96%',
                minHeight: '72%',
              }}
            >
              <View style={{ padding: theme.spacing(2), gap: theme.spacing(1), flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flex: 1 }}>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                      Choose a free time block
                    </ThemedText>
                    <Caption color={theme.colors.textSecondary} numberOfLines={2}>
                      For “{pickTimeTitle || 'this idea'}”
                      {pickTimeDate ? ` on ${formatDayLabel(pickTimeDate)}` : ''}
                    </Caption>
                  </View>
                  <Pressable
                    onPress={() => {
                      setPickTimeVisible(false);
                      pickTimeResolveRef.current?.(null);
                      pickTimeResolveRef.current = null;
                    }}
                    style={({ pressed }) => ({
                      padding: theme.spacing(0.5),
                      borderRadius: theme.radii.md,
                      opacity: pressed ? 0.7 : 1,
                    })}
                  >
                    <Ionicons name="close" size={22} color={theme.colors.text} />
                  </Pressable>
                </View>

                <ScrollView
                  style={{ flex: 1 }}
                  contentContainerStyle={{ paddingVertical: theme.spacing(1), gap: theme.spacing(1) }}
                  showsVerticalScrollIndicator
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled
                >
                  {(() => {
                    const base = coerceDate(pickTimeDate);
                    if (!base) return [];
                    const day = new Date(base);
                    day.setHours(0, 0, 0, 0);
                    const dayKey = day.getTime();

                    const parse = (t?: string | null): number | null => {
                      if (!t || t === 'flexible') return null;
                      const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
                      if (!m) return null;
                      const hh = Number(m[1]);
                      const mm = Number(m[2]);
                      if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
                      return hh * 60 + mm;
                    };
                    const fmt = (mins: number): string => {
                      const hh = Math.floor(mins / 60);
                      const mm = mins % 60;
                      return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
                    };
                    const fmtDur = (mins: number): string => {
                      const h = Math.floor(mins / 60);
                      const m = mins % 60;
                      if (h <= 0) return `${m}m`;
                      if (m === 0) return `${h}h`;
                      return `${h}h ${m}m`;
                    };

                    // busy intervals from itinerary items for the selected day
                    const busy: Array<{ start: number; end: number }> = [];
                    for (const it of safeItineraryItems) {
                      const itDay = coerceDate((it as any)?.startDate ?? null);
                      if (!itDay) continue;
                      const normalized = new Date(itDay);
                      normalized.setHours(0, 0, 0, 0);
                      if (normalized.getTime() !== dayKey) continue;
                      const start = parse((it as any)?.startTime ?? null);
                      if (start == null) continue;
                      const end = parse((it as any)?.endTime ?? null) ?? Math.min(start + 60, 24 * 60);
                      if (end <= start) continue;
                      busy.push({ start, end });
                    }

                    const windowStart = 8 * 60;
                    const windowEnd = 22 * 60;

                    if (busy.length === 0) {
                      return [
                        { startTime: '09:00', endTime: '11:00', label: '09:00–11:00 (2h)' },
                        { startTime: '12:00', endTime: '14:00', label: '12:00–14:00 (2h)' },
                        { startTime: '15:00', endTime: '17:00', label: '15:00–17:00 (2h)' },
                        { startTime: '18:00', endTime: '20:00', label: '18:00–20:00 (2h)' },
                      ];
                    }

                    busy.sort((a, b) => a.start - b.start);
                    const merged: Array<{ start: number; end: number }> = [];
                    for (const b of busy) {
                      if (!merged.length) {
                        merged.push({ ...b });
                        continue;
                      }
                      const last = merged[merged.length - 1];
                      if (b.start <= last.end) last.end = Math.max(last.end, b.end);
                      else merged.push({ ...b });
                    }

                    const free: Array<{ start: number; end: number }> = [];
                    let cursor = windowStart;
                    for (const b of merged) {
                      const bs = Math.max(b.start, windowStart);
                      const be = Math.min(b.end, windowEnd);
                      if (be <= windowStart || bs >= windowEnd) continue;
                      if (bs > cursor) free.push({ start: cursor, end: bs });
                      cursor = Math.max(cursor, be);
                    }
                    if (cursor < windowEnd) free.push({ start: cursor, end: windowEnd });

                    const blocks: Array<{ startTime: string; endTime: string; label: string }> = [];
                    for (const f of free) {
                      const dur = f.end - f.start;
                      if (dur < 60) continue;

                      // prefer 2h chunks for very large gaps
                      if (dur > 240) {
                        const aStart = f.start;
                        const aEnd = Math.min(f.start + 120, f.end);
                        blocks.push({
                          startTime: fmt(aStart),
                          endTime: fmt(aEnd),
                          label: `${fmt(aStart)}–${fmt(aEnd)} (${fmtDur(aEnd - aStart)})`,
                        });
                        const bStart = Math.min(f.start + 150, f.end - 60);
                        const bEnd = Math.min(bStart + 120, f.end);
                        if (bEnd - bStart >= 60) {
                          blocks.push({
                            startTime: fmt(bStart),
                            endTime: fmt(bEnd),
                            label: `${fmt(bStart)}–${fmt(bEnd)} (${fmtDur(bEnd - bStart)})`,
                          });
                        }
                        continue;
                      }

                      blocks.push({
                        startTime: fmt(f.start),
                        endTime: fmt(f.end),
                        label: `${fmt(f.start)}–${fmt(f.end)} (${fmtDur(dur)})`,
                      });
                    }

                    return blocks.slice(0, 10);
                  })().map((b) => (
                    <Pressable
                      key={b.label}
                      onPress={() => {
                        setPickTimeVisible(false);
                        pickTimeResolveRef.current?.({ startTime: b.startTime, endTime: b.endTime });
                        pickTimeResolveRef.current = null;
                      }}
                      style={({ pressed }) => ({
                        opacity: pressed ? 0.8 : 1,
                      })}
                    >
                      <View
                        style={{
                          paddingVertical: theme.spacing(1.25),
                          paddingHorizontal: theme.spacing(1.25),
                          borderRadius: theme.radii.lg,
                          borderWidth: 1,
                          borderColor: theme.colors.borderMuted,
                          backgroundColor: theme.colors.surface,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <View style={{ flex: 1 }}>
                          <ThemedText variant="bodyStrong">{b.label}</ThemedText>
                          <Caption color={theme.colors.textSecondary}>
                            Fits within your existing itinerary for the day
                          </Caption>
                        </View>
                        <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
                      </View>
                    </Pressable>
                  ))}

                  {(() => {
                    const base = coerceDate(pickTimeDate);
                    if (!base) return false;
                    const day = new Date(base);
                    day.setHours(0, 0, 0, 0);
                    const dayKey = day.getTime();

                    const parse = (t?: string | null): number | null => {
                      if (!t || t === 'flexible') return null;
                      const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
                      if (!m) return null;
                      const hh = Number(m[1]);
                      const mm = Number(m[2]);
                      if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
                      return hh * 60 + mm;
                    };

                    const busy: Array<{ start: number; end: number }> = [];
                    for (const it of safeItineraryItems) {
                      const itDay = coerceDate((it as any)?.startDate ?? null);
                      if (!itDay) continue;
                      const normalized = new Date(itDay);
                      normalized.setHours(0, 0, 0, 0);
                      if (normalized.getTime() !== dayKey) continue;
                      const start = parse((it as any)?.startTime ?? null);
                      if (start == null) continue;
                      const end = parse((it as any)?.endTime ?? null) ?? Math.min(start + 60, 24 * 60);
                      if (end <= start) continue;
                      busy.push({ start, end });
                    }

                    // If all day is booked (or gaps are too small), allow user to pick another day.
                    // We detect this by seeing if we rendered any block options (above). If none, show CTA.
                    // (The outer block list returns [] in that case.)
                    return busy.length > 0;
                  })() && (
                    <Pressable
                      onPress={() => {
                        setPickTimeVisible(false);
                        // Signal "pick another day" to the prompt loop
                        pickTimeResolveRef.current?.({});
                        pickTimeResolveRef.current = null;
                      }}
                      style={({ pressed }) => ({ opacity: pressed ? 0.8 : 1 })}
                    >
                      <View
                        style={{
                          paddingVertical: theme.spacing(1.25),
                          paddingHorizontal: theme.spacing(1.25),
                          borderRadius: theme.radii.lg,
                          borderWidth: 1,
                          borderColor: theme.colors.borderMuted,
                          backgroundColor: theme.colors.surface,
                        }}
                      >
                        <ThemedText variant="bodyStrong">Pick another day</ThemedText>
                        <Caption color={theme.colors.textSecondary}>
                          No free blocks left on this day.
                        </Caption>
                      </View>
                    </Pressable>
                  )}
                </ScrollView>
              </View>
            </Card>
          </View>
        )}
      </View>
    </Modal>
    )}
    </>
  );
}

function IdeasTabButton({
  label,
  active,
  count,
  onPress,
}: {
  label: string;
  active: boolean;
  count: number;
  onPress: () => void;
}) {
  const theme = useTheme();

  const parseColorToRgb = (value?: string): { r: number; g: number; b: number } | null => {
    const v = `${value || ''}`.trim();
    if (!v) return null;
    if (v.startsWith('#')) {
      const hex = v.slice(1);
      const full =
        hex.length === 3
          ? hex
              .split('')
              .map((c) => c + c)
              .join('')
          : hex.length === 6
            ? hex
            : null;
      if (!full) return null;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      if ([r, g, b].some((n) => Number.isNaN(n))) return null;
      return { r, g, b };
    }
    const m = v.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (m) {
      return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) };
    }
    return null;
  };

  const getContrastText = (bg: string): '#000' | '#fff' => {
    const rgb = parseColorToRgb(bg);
    if (!rgb) return '#fff';
    // relative luminance (sRGB)
    const toLin = (c: number) => {
      const s = c / 255;
      return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    const L = 0.2126 * toLin(rgb.r) + 0.7152 * toLin(rgb.g) + 0.0722 * toLin(rgb.b);
    return L > 0.6 ? '#000' : '#fff';
  };

  const badgeBg = active ? theme.colors.primary : theme.colors.primary + '22';
  const badgeText = active ? getContrastText(badgeBg) : theme.colors.primary;

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        // Flex 1 removed to allow intrinsic width in ScrollView
        flexShrink: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        paddingHorizontal: theme.spacing(2),
        paddingVertical: theme.spacing(1),
        minHeight: 36,
        borderRadius: theme.radii.pill,
        borderWidth: 1,
        borderColor: active ? theme.colors.primary : theme.colors.borderMuted,
        backgroundColor: active ? theme.colors.primary + '10' : 'transparent',
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <ThemedText
        variant="bodyStrong"
        allowFontScaling={false}
        style={{
          fontSize: 13,
          fontWeight: '700',
          color: active ? theme.colors.primary : theme.colors.text,
        }}
      >
        {label}
      </ThemedText>
      {count > 0 ? (
        <View
          style={{
            minWidth: 18,
            paddingHorizontal: theme.spacing(0.5),
            borderRadius: theme.radii.pill,
            backgroundColor: badgeBg,
            borderWidth: active ? 0 : 1,
            borderColor: theme.colors.primary + '35',
          }}
        >
          <Caption
            allowFontScaling={false}
            color={badgeText}
            style={{ textAlign: 'center', fontSize: 11, fontWeight: '800' }}
          >
            {count}
          </Caption>
        </View>
      ) : null}
    </Pressable>
  );
}

const stripHtmlTags = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

function inferLocalPickCategory(type: string, name: string): ItineraryItemCategory {
  const lowerName = name.toLowerCase();
  if (type === 'hotel') return 'hotel';
  if (type === 'flight') return 'transportation';
  if (lowerName.includes('museum') || lowerName.includes('gallery')) return 'activity';
  if (
    lowerName.includes('restaurant') ||
    lowerName.includes('cafe') ||
    lowerName.includes('dining')
  ) {
    return 'restaurant';
  }
  if (lowerName.includes('tour') || lowerName.includes('excursion')) return 'activity';
  return 'activity';
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'poi':
      return 'location-outline';
    case 'activity':
      return 'accessibility-outline';
    case 'flight':
      return 'airplane-outline';
    case 'hotel':
      return 'bed-outline';
    default:
      return 'star-outline';
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'poi':
      return 'Point of interest';
    case 'activity':
      return 'Activity';
    case 'flight':
      return 'Flight';
    case 'hotel':
      return 'Hotel';
    default:
      return 'Recommendation';
  }
};

const getRecommendationImageUrl = (item: LocalPicksRecommendationItem): string | undefined => {
  const meta = (item as any)?.metadata ?? {};
  const firstFromArray = (value: any): any => (Array.isArray(value) && value.length ? value[0] : undefined);

  const unwrapMaybeUrl = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      const v =
        (value as any).url ||
        (value as any).uri ||
        (value as any).photoUri ||
        (value as any).photoUrl ||
        (value as any).imageUrl;
      return typeof v === 'string' ? v : undefined;
    }
    return undefined;
  };

  const candidate =
    item.imageUrl ||
    meta.imageUrl ||
    meta.photoUrl ||
    meta.photoUri ||
    meta.photo ||
    meta.image ||
    unwrapMaybeUrl(firstFromArray(meta.images)) ||
    unwrapMaybeUrl(firstFromArray(meta.photos)) ||
    unwrapMaybeUrl(firstFromArray(meta.photoUrls)) ||
    unwrapMaybeUrl(firstFromArray(meta.photoUris));

  const resolved = unwrapMaybeUrl(candidate);
  return typeof resolved === 'string' && resolved.trim().length > 0 ? resolved : undefined;
};

const getCategoryLabel = (item: LocalPicksRecommendationItem): string | undefined => {
  const meta = (item as any)?.metadata ?? {};
  const raw =
    item.category ||
    meta.category ||
    meta.subType ||
    (Array.isArray(meta.tags) ? meta.tags[0] : undefined);
  if (!raw || typeof raw !== 'string') return undefined;
  const normalized = raw.trim().toLowerCase();
  const map: Record<string, string> = {
    dining: 'Cuisine',
    food: 'Cuisine',
    restaurant: 'Cuisine',
    cuisine: 'Cuisine',
    landmarks: 'Landmark',
    landmark: 'Landmark',
    museum: 'Museum',
    museums: 'Museum',
    culture: 'Culture',
    cultural: 'Culture',
    trendy: 'Trendy',
    nightlife: 'Nightlife',
    shopping: 'Shopping',
    nature: 'Nature',
    outdoors: 'Outdoors',
  };
  if (map[normalized]) return map[normalized];
  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
};

const getCategoryTone = (
  label?: string,
): { color: string; icon: keyof typeof Ionicons.glyphMap } | null => {
  if (!label) return null;
  const type = mapCategoryToActivityType(label);
  switch (type) {
    case 'food':
      return { color: '#FF6B6B', icon: 'restaurant-outline' };
    case 'culture':
      return { color: '#4ECDC4', icon: 'library-outline' };
    case 'adventure':
      return { color: '#45B7D1', icon: 'bicycle-outline' };
    case 'nature':
      return { color: '#96CEB4', icon: 'leaf-outline' };
    case 'nightlife':
      return { color: '#FFEAA7', icon: 'wine-outline' };
    case 'shopping':
      return { color: '#DDA15E', icon: 'bag-outline' };
    case 'relaxation':
      return { color: '#A8DADC', icon: 'heart-outline' };
    case 'historical':
      return { color: '#B19CD9', icon: 'library-outline' };
    default:
      // Trendy / other: give it a distinct pop without overfitting.
      return { color: '#8D99AE', icon: 'sparkles-outline' };
  }
};
